import {
  Alert,
  Autocomplete,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Fragment, useCallback, useMemo, useRef, useState } from 'react';
import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router';

import {
  ArrowLeft,
  CalendarBlank,
  ChevronDown,
  Close,
  Eye,
  HelpCircle,
  ClipboardListOutline,
  LockOutline,
  Pencil,
  Plus,
  Printer,
  TrashCan,
  TrayArrowUp,
} from '@opengov/react-capital-assets';

import { CaseStatusBadge } from '../components/vcm/CaseStatusBadge';
import { ResourcePickerDrawer } from '../components/vcm/ResourcePickerDrawer';
import { SignaturePad } from '../components/vcm/SignaturePad';
import { TierBadge } from '../components/vcm/TierBadge';
import { WorkOrdersPanel } from '../components/vcm/WorkOrdersPanel';
import { useVcm } from '../contexts/VcmContext';
import { useAppNavigation } from '../hooks/useAppNavigation';
import { computeComposition, formatDob } from '../lib/vcm/computed';
import {
  APPEAL_STATUSES,
  CASE_PROGRESSIONS,
  CASE_STATUSES,
  CASE_TABS,
  CASE_TIERS,
  CLAIM_STATUSES,
  CLIENT_CONNECT_STATUSES,
  CLOSURE_CHECKLIST_ITEMS,
  CLOSURE_GUIDANCE,
  CLOSURE_REASONS,
  CO_APPLICANT_RELATIONSHIPS,
  CONTACT_INITIATED_BY,
  CONTACT_LEVELS,
  DAMAGE_LEVELS,
  DOCUMENT_TYPES,
  ETHNICITY_OPTIONS,
  FEMA_STATUSES,
  FOLLOW_UP_TYPES,
  GOAL_STATUSES,
  RECOVERY_TIMELINE_OPTIONS,
  HOUSEHOLD_CONDITIONS,
  HOUSEHOLD_RELATIONSHIPS,
  INSURANCE_ADEQUACY_OPTIONS,
  INSURANCE_DEDUCTIBLE_RANGES,
  INSURANCE_TYPES,
  ISTAT_PHOTO_MAX,
  LTRG_STATUSES,
  NOTE_TYPES,
  OWNER_RENTER_OPTIONS,
  PERMANENT_RESIDENCE_STATUSES,
  RACE_OPTIONS,
  RECURRENCE_OPTIONS,
  REFERRAL_STATUSES,
  SBA_STATUSES,
  SHELTER_SERVICE_NEEDS,
  SHELTER_STATUSES,
  STATUS_DEFINITIONS,
  TEMP_HOUSING_STATUSES,
  TIER_DEFINITIONS,
} from '../lib/vcm/constants';
import { TEXAS_COUNTIES } from '../lib/vcm/counties';
import { getOptionsArray, RECOVERY_NEEDS_OPTIONS } from '../lib/vcm/option-maps';
/* MOCK DATA — replace with real /v2 API calls when backend is connected */
import {
  mockGetAuditByCaseId,
  mockGetCaseById,
  mockGetClientById,
  mockGetDocumentsByCaseId,
  mockGetFollowUp,
  mockGetGoalsByCaseId,
  mockGetHouseholdByClientId,
  mockGetInteractionsByCaseId,
  mockGetReferralsByCaseId,
  mockGetResources,
} from '../mocks/vcm-mock-data';
import type {
  CaseTabId,
  FollowUp,
  GoalNote,
  InsuranceEntry,
  TcrAuditEntry,
  TcrCase,
  TcrClient,
  TcrDocument,
  TcrGoal,
  TcrHouseholdMember,
  TcrInteraction,
  TcrReferral,
  TcrResource,
} from '../types/vcm';

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

const MS_PER_DAY = 1000 * 60 * 60 * 24;

function daysUntil(dateStr: string | null): number | null {
  if (!dateStr) return null;
  const ts = Date.parse(dateStr);
  if (Number.isNaN(ts)) return null;
  return Math.ceil((ts - Date.now()) / MS_PER_DAY);
}

/** Parse the day count from a timeline label like "3 months (90 days)" */
function parseTimelineDays(value: string): number | null {
  const m = /\((\d+) days?\)/.exec(value);
  const raw = m?.[1];
  return raw != null ? parseInt(raw, 10) : null;
}

const RECOVERY_NEED_LABELS = getOptionsArray(RECOVERY_NEEDS_OPTIONS).map((o) => o.label);

/** Mock save: flips the global save indicator. Edits stay local (no backend). */
function useMockSave() {
  const { setSaveStatus } = useVcm();
  return useCallback(() => {
    setSaveStatus('saving');
    window.setTimeout(() => setSaveStatus('saved'), 600);
  }, [setSaveStatus]);
}

interface LabelValueProps {
  label: string;
  value: string | number | null | undefined;
}

function LabelValue({ label, value }: LabelValueProps) {
  return (
    <Box sx={{ mb: (t) => t.spacing(2) }}>
      <Typography
        variant="caption"
        sx={{ color: (t) => t.palette.text.secondary, display: 'block' }}
      >
        {label}
      </Typography>
      <Typography variant="body2">{value ?? '—'}</Typography>
    </Box>
  );
}

/** Labeled select built on MUI TextField. */
function SelectField({
  label,
  value,
  onChange,
  options,
  allowEmpty = true,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: readonly string[];
  allowEmpty?: boolean;
}) {
  return (
    <TextField
      select
      size="small"
      fullWidth
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {allowEmpty && <MenuItem value="">—</MenuItem>}
      {options.map((o) => (
        <MenuItem key={o} value={o}>
          {o}
        </MenuItem>
      ))}
    </TextField>
  );
}

/** Labeled text/date/number input. */
function InputField({
  label,
  value,
  onChange,
  type = 'text',
  multiline = false,
  rows,
  placeholder,
  min,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  multiline?: boolean;
  rows?: number;
  placeholder?: string;
  min?: string | undefined;
}) {
  if (multiline) {
    return (
      <TextField
        fullWidth
        label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        multiline
        rows={rows ?? 4}
        placeholder={placeholder}
      />
    );
  }
  return (
    <TextField
      size="small"
      fullWidth
      label={label}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      slotProps={type === 'date' ? { inputLabel: { shrink: true }, htmlInput: { min } } : undefined}
    />
  );
}

function SaveBar({ onSave, label = 'Save changes' }: { onSave: () => void; label?: string }) {
  return (
    <Box sx={{ mt: (t) => t.spacing(2) }}>
      <Button variant="contained" onClick={onSave}>
        {label}
      </Button>
    </Box>
  );
}

interface MetricMiniCardProps {
  title: string;
  value: number | string;
  subtitle?: string;
}

function MetricMiniCard({ title, value, subtitle }: MetricMiniCardProps) {
  return (
    <Card variant="outlined">
      <CardContent sx={{ textAlign: 'center', py: (t) => t.spacing(1.5), px: (t) => t.spacing(1), '&:last-child': { pb: (t) => t.spacing(1.5) } }}>
        <Typography variant="h5" sx={{ color: (t) => t.palette.primary.main, fontWeight: 700 }}>
          {value}
        </Typography>
        <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary, fontWeight: 600 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" sx={{ display: 'block', color: (t) => t.palette.text.disabled, fontSize: 10 }}>
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

interface TabPanelProps {
  activeTab: CaseTabId;
  tabId: CaseTabId;
  children: React.ReactNode;
}

function TabPanel({ activeTab, tabId, children }: TabPanelProps) {
  if (activeTab !== tabId) return null;
  return (
    <Box role="tabpanel" sx={{ pt: (t) => t.spacing(3) }}>
      {children}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Computed case alerts
// ---------------------------------------------------------------------------

interface CaseAlert {
  severity: 'error' | 'warning' | 'info';
  message: string;
}

function computeCaseAlerts(
  caseRecord: TcrCase,
  followUp: FollowUp | null,
  referrals: TcrReferral[],
): CaseAlert[] {
  const alerts: CaseAlert[] = [];

  const followUpDays = daysUntil(followUp?.nextFollowUpDate ?? null);
  if (followUpDays !== null && followUpDays < 0) {
    alerts.push({
      severity: 'error',
      message: `Follow-up is ${Math.abs(followUpDays)} day(s) overdue.`,
    });
  } else if (followUpDays !== null && followUpDays <= 3) {
    alerts.push({
      severity: 'warning',
      message: `Follow-up is due in ${followUpDays} day(s).`,
    });
  }

  const housingDays = daysUntil(caseRecord.temporaryHousingEndDate);
  if (housingDays !== null && housingDays < 0) {
    alerts.push({
      severity: 'error',
      message: `Temporary housing expired ${Math.abs(housingDays)} day(s) ago.`,
    });
  } else if (housingDays !== null && housingDays <= 30) {
    alerts.push({
      severity: 'warning',
      message: `Temporary housing expires in ${housingDays} day(s).`,
    });
  }

  if (caseRecord.housingCondition === 'Major' || caseRecord.housingCondition === 'Destroyed') {
    alerts.push({
      severity: 'warning',
      message: `Damage level recorded as ${caseRecord.housingCondition}.`,
    });
  }

  if (!caseRecord.releaseOfInfoUploaded) {
    alerts.push({
      severity: 'info',
      message: 'Release of information has not been uploaded.',
    });
  }

  const openReferrals = referrals.filter((r) => ['Requested', 'Referred'].includes(r.status)).length;
  if (openReferrals > 0) {
    alerts.push({
      severity: 'info',
      message: `${openReferrals} referral(s) awaiting outcome.`,
    });
  }

  return alerts;
}

// ---------------------------------------------------------------------------
// Summary tab (read-only dashboard)
// ---------------------------------------------------------------------------

function SummaryTab({
  caseRecord,
  client,
  goals,
  referrals,
  documents,
  interactions,
  audit,
  composition,
}: {
  caseRecord: TcrCase;
  client: TcrClient | null;
  goals: TcrGoal[];
  referrals: TcrReferral[];
  documents: TcrDocument[];
  interactions: TcrInteraction[];
  audit: TcrAuditEntry[];
  composition: { adults: number; children: number; seniors: number; total: number };
}) {
  const flags: string[] = [];
  if (client?.unsafeUnsanitaryHousing) flags.push('Unsafe/Unsanitary Housing');
  if (client?.significantEmotionalDistress) flags.push('Significant Emotional Distress');
  if (client?.accessOrFunctionalNeeds) flags.push('Access/Functional Needs');
  if (client?.singleHeadWithMinors) flags.push('Single Head w/ Minors');
  if (client?.headOfHousehold65OrOlder) flags.push('Head of Household 65+');

  const unmetGoals = goals.filter((g) => !g.complete).length;
  const openReferrals = referrals.filter(
    (r) => !['Provided', 'Declined', 'Closed'].includes(r.status),
  ).length;

  const addr = client?.primaryAddress;
  const addressQuery = addr
    ? [addr.address1, addr.city, addr.state, addr.zip].filter(Boolean).join(', ')
    : null;
  const addressDisplay = addr
    ? [addr.address1, addr.city, addr.state, addr.zip].filter(Boolean).join(', ')
    : 'No address on file';
  const latestNotes = interactions.slice(0, 3);
  const sortedAudit = [...audit].sort((a, b) => (a.timestamp < b.timestamp ? 1 : -1)).slice(0, 5);

  return (
    <Stack spacing={2}>
      {/* Row 1: Flags banner */}
      <Card variant="outlined">
        <CardContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {flags.length > 0 ? (
              flags.map((f) => <Chip key={f} label={f} color="warning" size="small" />)
            ) : (
              <Typography variant="caption" sx={{ color: (t) => t.palette.success.main, fontWeight: 600 }}>No active vulnerability flags</Typography>
            )}
            <Box sx={{ flex: 1 }} />
            {caseRecord.housingCondition && (
              <Chip label={`Damage: ${caseRecord.housingCondition}`} size="small" color={caseRecord.housingCondition === 'Major' || caseRecord.housingCondition === 'Destroyed' ? 'error' : 'warning'} />
            )}
            {caseRecord.femaApplicationStatus && (
              <Chip label={`VA Claim: ${caseRecord.femaApplicationStatus}`} size="small" color={caseRecord.femaApplicationStatus === 'Approved' ? 'success' : caseRecord.femaApplicationStatus === 'Denied' ? 'error' : 'warning'} />
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Row 2: 6 metric tiles */}
      <Grid container spacing={1.5}>
        <Grid size={{ xs: 4, sm: 2 }}><MetricMiniCard title="Household" value={composition.total} subtitle={`${composition.adults}A · ${composition.children}C · ${composition.seniors}S`} /></Grid>
        <Grid size={{ xs: 4, sm: 2 }}><MetricMiniCard title="Unmet Goals" value={unmetGoals} subtitle={`of ${goals.length} total`} /></Grid>
        <Grid size={{ xs: 4, sm: 2 }}><MetricMiniCard title="Open Referrals" value={openReferrals} subtitle={`of ${referrals.length} total`} /></Grid>
        <Grid size={{ xs: 4, sm: 2 }}><MetricMiniCard title="Documents" value={documents.length} subtitle="on file" /></Grid>
        <Grid size={{ xs: 4, sm: 2 }}><MetricMiniCard title="VA Award" value={caseRecord.femaTotalAmountReceived ?? '--'} subtitle={caseRecord.femaApplicationStatus ?? 'N/A'} /></Grid>
        <Grid size={{ xs: 4, sm: 2 }}><MetricMiniCard title="Insurance" value={caseRecord.insuranceEntries.length} subtitle={caseRecord.insuranceEntries.length > 0 ? caseRecord.insuranceEntries.map((e) => e.coverageType).join(', ') : 'No policies'} /></Grid>
      </Grid>

      {/* Row 3: Map | Housing | Insurance — 3-column */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card variant="outlined" sx={{ height: '100%', overflow: 'hidden' }}>
            <Box sx={{ height: 220, bgcolor: 'action.hover' }}>
              {addressQuery ? (
                <iframe title="Property location" style={{ width: '100%', height: '100%', border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" src={`https://maps.google.com/maps?q=${encodeURIComponent(addressQuery)}&t=&z=15&ie=UTF8&iwloc=&output=embed`} allowFullScreen />
              ) : (
                <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
                  <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>No address</Typography>
                </Stack>
              )}
            </Box>
            <CardContent sx={{ px: 2, py: 1, '&:last-child': { pb: 1 } }}>
              <Typography variant="overline" sx={{ fontSize: 10, color: (t) => t.palette.text.secondary }}>Prior Living Situation Address</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{addressDisplay}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="subtitle2" sx={{ pb: 1, mb: 1, borderBottom: 1, borderColor: 'divider' }}>Housing</Typography>
              <Stack spacing={1}>
                <LabelValue label="Prior Living Situation" value={caseRecord.priorLivingSituation ?? '—'} />
                <LabelValue label="Current Residence" value={caseRecord.currentResidence ?? '—'} />
                <LabelValue label="Permanent Status" value={caseRecord.permanentResidenceStatus ?? '—'} />
                <LabelValue label="Housing Condition" value={caseRecord.housingCondition ?? 'Not assessed'} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="subtitle2" sx={{ pb: 1, mb: 1, borderBottom: 1, borderColor: 'divider' }}>Insurance</Typography>
              {caseRecord.insuranceEntries.length === 0 ? (
                <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>No policies on file</Typography>
              ) : (
                <Stack spacing={1}>
                  {caseRecord.insuranceEntries.map((entry) => (
                    <Stack key={entry.id} direction="row" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{entry.coverageType}</Typography>
                        {entry.company && <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>{entry.company}</Typography>}
                      </Box>
                      <Chip label={entry.claimStatus ?? '—'} size="small" color={entry.claimStatus === 'Approved' ? 'success' : entry.claimStatus === 'Denied' ? 'error' : 'warning'} />
                    </Stack>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Row 4: Goals+Referrals+Household | Notes+Activity | Assistance+Documents — 3-column */}
      <Grid container spacing={2}>
        {/* Column 1 */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Stack spacing={2}>
            <Card variant="outlined">
              <CardContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="subtitle2" sx={{ pb: 1, mb: 1, borderBottom: 1, borderColor: 'divider' }}>Recovery Goals</Typography>
                {goals.length === 0 ? <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>No goals created</Typography> : (
                  <Stack spacing={1}>
                    {goals.map((g) => (
                      <Stack key={g.id} direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" sx={{ fontWeight: 500, textDecoration: g.status === 'Goal Met' ? 'line-through' : undefined, color: g.status === 'Goal Met' ? 'text.secondary' : undefined }}>{g.name}</Typography>
                        <CaseStatusBadge status={g.status} />
                      </Stack>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
            <Card variant="outlined">
              <CardContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="subtitle2" sx={{ pb: 1, mb: 1, borderBottom: 1, borderColor: 'divider' }}>Referrals</Typography>
                {referrals.length === 0 ? <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>No referrals</Typography> : (
                  <Stack spacing={1}>
                    {referrals.map((r) => (
                      <Stack key={r.id} direction="row" justifyContent="space-between" alignItems="center">
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{r.serviceName}</Typography>
                          <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>{r.agencyName}</Typography>
                        </Box>
                        <CaseStatusBadge status={r.status} />
                      </Stack>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Stack>
        </Grid>
        {/* Column 2 */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Stack spacing={2}>
            <Card variant="outlined">
              <CardContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="subtitle2" sx={{ pb: 1, mb: 1, borderBottom: 1, borderColor: 'divider' }}>Case Notes</Typography>
                {latestNotes.length === 0 ? <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>No case notes</Typography> : (
                  <Stack spacing={1.5}>
                    {latestNotes.map((n) => (
                      <Box key={n.id}>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="caption" sx={{ fontWeight: 700 }}>{n.noteType}</Typography>
                          <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>{new Date(n.createdAt).toLocaleDateString()}</Typography>
                        </Stack>
                        <Typography variant="body2" sx={{ color: (t) => t.palette.text.secondary, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{n.narrative}</Typography>
                      </Box>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
            <Card variant="outlined">
              <CardContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="subtitle2" sx={{ pb: 1, mb: 1, borderBottom: 1, borderColor: 'divider' }}>Recent Activity</Typography>
                <Stack spacing={1}>
                  {sortedAudit.map((entry) => (
                    <Box key={entry.id}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>{entry.action}</Typography>
                        <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>{new Date(entry.timestamp).toLocaleDateString()}</Typography>
                      </Stack>
                      {entry.details && <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>{entry.details}</Typography>}
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
        {/* Column 3 */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Stack spacing={2}>
            <Card variant="outlined">
              <CardContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="subtitle2" sx={{ pb: 1, mb: 1, borderBottom: 1, borderColor: 'divider' }}>Assistance</Typography>
                <Grid container spacing={1}>
                  <Grid size={{ xs: 6 }}><LabelValue label="VA Claim Status" value={caseRecord.femaApplicationStatus ?? 'N/A'} /></Grid>
                  <Grid size={{ xs: 6 }}><LabelValue label="VA Award" value={caseRecord.femaTotalAmountReceived ?? '—'} /></Grid>
                  <Grid size={{ xs: 6 }}><LabelValue label="Education Benefits" value={caseRecord.sbaLoanStatus ?? 'N/A'} /></Grid>
                  <Grid size={{ xs: 6 }}><LabelValue label="Partner Referral" value={caseRecord.ltrgCaseStatus ?? 'N/A'} /></Grid>
                  <Grid size={{ xs: 6 }}><LabelValue label="Income" value={caseRecord.grossMonthlyIncome ?? '—'} /></Grid>
                  <Grid size={{ xs: 6 }}><LabelValue label="Expenses" value={caseRecord.monthlyExpenses ?? '—'} /></Grid>
                </Grid>
              </CardContent>
            </Card>
            <Card variant="outlined">
              <CardContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="subtitle2" sx={{ pb: 1, mb: 1, borderBottom: 1, borderColor: 'divider' }}>Documents</Typography>
                {documents.length === 0 ? <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>No documents uploaded</Typography> : (
                  <Stack spacing={0.5}>
                    {documents.map((doc) => (
                      <Stack key={doc.id} direction="row" justifyContent="space-between">
                        <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>{doc.fileName}</Typography>
                        <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary, flexShrink: 0, ml: 1 }}>{doc.fileSize}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
}

function AuditTab({ audit }: { audit: TcrAuditEntry[] }) {
  return (
    <Card variant="outlined">
      <CardContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="subtitle2" sx={{ pb: 1, mb: 1.5, borderBottom: 1, borderColor: "divider" }}>
          Audit Log ({audit.length})
        </Typography>
        <Box sx={{ maxHeight: 600, overflowY: 'auto' }}>
          <Stack spacing={0}>
            {audit.map((entry, idx) => (
              <Stack
                key={entry.id}
                direction="row"
                spacing={2}
                justifyContent="space-between"
                sx={{
                  py: (t) => t.spacing(1.25),
                  borderBottom: (t) =>
                    idx < audit.length - 1 ? `1px solid ${t.palette.divider}` : 'none',
                }}
              >
                <Typography variant="body2">
                  {entry.action}{' '}
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{ color: (t) => t.palette.text.secondary }}
                  >
                    ({entry.performer})
                  </Typography>
                  {entry.details ? (
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', color: (t) => t.palette.text.secondary }}
                    >
                      {entry.details}
                    </Typography>
                  ) : null}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: (t) => t.palette.text.secondary, whiteSpace: 'nowrap' }}
                >
                  {new Date(entry.timestamp).toLocaleString()}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Case Profile tab — editable selects + definitions sheet
// ---------------------------------------------------------------------------

function CaseProfileTab({ caseRecord }: { caseRecord: TcrCase }) {
  const save = useMockSave();
  const [form, setForm] = useState({
    status: caseRecord.status as string,
    progression: caseRecord.progression as string,
    tier: caseRecord.tier as string,
    assignedManager: caseRecord.assignedManager,
    managerOrganization: caseRecord.managerOrganization,
    supervisor: caseRecord.supervisor,
    contactLevel: caseRecord.contactLevel,
  });
  const [helpOpen, setHelpOpen] = useState(false);
  const set = (k: keyof typeof form, v: string) => setForm((p) => ({ ...p, [k]: v }));

  return (
    <Stack spacing={2}>
      <Card variant="outlined">
        <CardContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ pb: 1, mb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2">Case profile</Typography>
            <Button size="small" startIcon={<HelpCircle />} onClick={() => setHelpOpen(true)}>
              Definitions
            </Button>
          </Stack>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <SelectField label="Event" value={caseRecord.program ?? ''} onChange={() => {}} options={[caseRecord.program ?? '']} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <SelectField label="Case Status" value={form.status} onChange={(v) => set('status', v)} options={CASE_STATUSES} allowEmpty={false} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <SelectField label="Case Progression" value={form.progression} onChange={(v) => set('progression', v)} options={CASE_PROGRESSIONS} allowEmpty={false} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <SelectField label="Tier" value={form.tier} onChange={(v) => set('tier', v)} options={CASE_TIERS} allowEmpty={false} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <InputField label="Assigned Case Manager" value={form.assignedManager} onChange={(v) => set('assignedManager', v)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <InputField label="Case Manager Organization" value={form.managerOrganization} onChange={(v) => set('managerOrganization', v)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <InputField label="DCM Supervisor" value={form.supervisor} onChange={(v) => set('supervisor', v)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <SelectField label="DCM Level of Client Contact" value={form.contactLevel} onChange={(v) => set('contactLevel', v)} options={CONTACT_LEVELS} />
            </Grid>
          </Grid>
          <SaveBar onSave={save} />
        </CardContent>
      </Card>

      <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} maxWidth="sm" fullWidth scroll="paper">
        <DialogTitle>
          Definitions
          <IconButton
            aria-label="Close"
            onClick={() => setHelpOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" gutterBottom>
            Tiers
          </Typography>
          {(Object.entries(TIER_DEFINITIONS) as [string, string][]).map(([tier, def]) => (
            <Box key={tier} sx={{ mb: (t) => t.spacing(1.5) }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {tier}
              </Typography>
              <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>
                {def}
              </Typography>
            </Box>
          ))}
          <Divider sx={{ my: (t) => t.spacing(2) }} />
          <Typography variant="subtitle2" gutterBottom>
            Statuses
          </Typography>
          {(Object.entries(STATUS_DEFINITIONS) as [string, string][]).map(([status, def]) => (
            <Box key={status} sx={{ mb: (t) => t.spacing(1.5) }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {status}
              </Typography>
              <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>
                {def}
              </Typography>
            </Box>
          ))}
          <Divider sx={{ my: (t) => t.spacing(2) }} />
          <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>
            {CLOSURE_GUIDANCE}
          </Typography>
        </DialogContent>
      </Dialog>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Client Intake tab — editable client + intake + consent + signature
// ---------------------------------------------------------------------------

function ClientIntakeTab({
  caseRecord,
  client,
  verifierName,
  household,
  clientId,
}: {
  caseRecord: TcrCase;
  client: TcrClient | null;
  verifierName: string;
  household: TcrHouseholdMember[];
  clientId: string;
}) {
  const save = useMockSave();
  const [clientForm, setClientForm] = useState({
    firstName: client?.firstName ?? '',
    middleName: client?.middleName ?? '',
    lastName: client?.lastName ?? '',
    nickname: client?.nickname ?? '',
    dateOfBirth: client?.dateOfBirth ?? '',
    gender: client?.gender ?? '',
    phoneHome: client?.phoneHome ?? '',
    phoneAlternate: client?.phoneAlternate ?? '',
    email: client?.email ?? '',
    coApplicantName: client?.coApplicantName ?? '',
    coApplicantPhone: client?.coApplicantPhone ?? '',
  });
  const setC = (k: keyof typeof clientForm, v: string) =>
    setClientForm((p) => ({ ...p, [k]: v }));
  const [coAppRels, setCoAppRels] = useState<string[]>(
    client?.coApplicantRelationship
      ? client.coApplicantRelationship
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      : [],
  );

  const [intake, setIntake] = useState({
    dateOfDcmIntake: caseRecord.dateOfDcmIntake ?? '',
    referralSource: caseRecord.referralSource ?? '',
    dateOfArrival: caseRecord.dateOfArrival ?? '',
    contactInitiatedBy: caseRecord.contactInitiatedBy ?? '',
    intakeOrganization: caseRecord.intakeOrganization ?? '',
    intakeLocation: caseRecord.intakeLocation ?? '',
    clientRecoveryStatus: caseRecord.clientRecoveryStatus ?? '',
    vaFileNumber: caseRecord.vaFileNumber ?? '',
    grossMonthlyIncome: caseRecord.grossMonthlyIncome ?? '',
    monthlyExpenses: caseRecord.monthlyExpenses ?? '',
  });
  const setI = (k: keyof typeof intake, v: string) => setIntake((p) => ({ ...p, [k]: v }));

  const [consent, setConsent] = useState({
    femaReleaseUploaded: caseRecord.femaReleaseUploaded,
    rightsResponsibilitiesUploaded: caseRecord.rightsResponsibilitiesUploaded,
    verbalConsentRecorded: caseRecord.verbalConsentRecorded,
    authorizationToShare: caseRecord.authorizationToShare,
    ninetyDayNotice: caseRecord.ninetyDayNotice,
    organizationOptOut: caseRecord.organizationOptOut,
    revokingConsent: caseRecord.revokingConsent,
  });
  const setConsentField = (k: keyof typeof consent, v: boolean) =>
    setConsent((p) => ({ ...p, [k]: v }));

  const [releaseVerified, setReleaseVerified] = useState(caseRecord.releaseOfInfoUploaded);
  const [verifiedStamp, setVerifiedStamp] = useState<string | null>(
    caseRecord.releaseOfInfoUploaded ? 'On record' : null,
  );
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleReleaseToggle = (checked: boolean) => {
    if (checked && !releaseVerified) {
      setConfirmOpen(true);
      return;
    }
    if (!checked) {
      setReleaseVerified(false);
      setVerifiedStamp(null);
    }
  };

  const confirmRelease = () => {
    setReleaseVerified(true);
    setVerifiedStamp(`Verified by ${verifierName} on ${new Date().toLocaleString()}`);
    setConfirmOpen(false);
  };

  // ── Household state ──
  const [hhRows, setHhRows] = useState<TcrHouseholdMember[]>(household);
  const [hhOpen, setHhOpen] = useState(false);
  const [hhDraft, setHhDraft] = useState<MemberDraft>(EMPTY_MEMBER);
  const openAddMember = () => { setHhDraft(EMPTY_MEMBER); setHhOpen(true); };
  const openEditMember = (m: TcrHouseholdMember) => {
    setHhDraft({
      id: m.id, firstName: m.firstName, lastName: m.lastName,
      relationship: m.relationship, dateOfBirth: m.dateOfBirth ?? '',
      condition: m.condition, race: m.race ?? '', ethnicity: m.ethnicity ?? '',
      shelterServiceNeeds: [...m.shelterServiceNeeds],
    });
    setHhOpen(true);
  };
  const toggleHhNeed = (need: string) =>
    setHhDraft((p) => ({
      ...p,
      shelterServiceNeeds: p.shelterServiceNeeds.includes(need)
        ? p.shelterServiceNeeds.filter((n) => n !== need)
        : [...p.shelterServiceNeeds, need],
    }));
  const saveHhMember = () => {
    const now = new Date().toISOString();
    if (hhDraft.id) {
      setHhRows((prev) => prev.map((m) => m.id === hhDraft.id
        ? { ...m, firstName: hhDraft.firstName, lastName: hhDraft.lastName, relationship: hhDraft.relationship, dateOfBirth: hhDraft.dateOfBirth || null, condition: hhDraft.condition, race: hhDraft.race || null, ethnicity: hhDraft.ethnicity || null, shelterServiceNeeds: hhDraft.shelterServiceNeeds, updatedAt: now }
        : m));
    } else {
      setHhRows((prev) => [...prev, { id: `hh-${Date.now()}`, clientId, firstName: hhDraft.firstName, lastName: hhDraft.lastName, relationship: hhDraft.relationship, dateOfBirth: hhDraft.dateOfBirth || null, condition: hhDraft.condition, race: hhDraft.race || null, ethnicity: hhDraft.ethnicity || null, shelterServiceNeeds: hhDraft.shelterServiceNeeds, createdAt: now, updatedAt: now }]);
    }
    setHhOpen(false);
  };

  return (
    <Stack spacing={2}>
      {/* 1. Initial Intake Information */}
      <Card variant="outlined">
        <CardContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="subtitle2" sx={{ pb: 1, mb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            Initial intake information
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}><InputField label="Date of DCM Intake" type="date" value={intake.dateOfDcmIntake} onChange={(v) => setI('dateOfDcmIntake', v)} /></Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}><InputField label="Who Referred the Client" value={intake.referralSource} onChange={(v) => setI('referralSource', v)} /></Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}><InputField label="Other Referral Source" value="" onChange={() => {}} /></Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}><InputField label="Date of Arrival" type="date" value={intake.dateOfArrival} onChange={(v) => setI('dateOfArrival', v)} /></Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}><SelectField label="Contact Initiated By" value={intake.contactInitiatedBy} onChange={(v) => setI('contactInitiatedBy', v)} options={CONTACT_INITIATED_BY} /></Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}><InputField label="Intake Organization" value={intake.intakeOrganization} onChange={(v) => setI('intakeOrganization', v)} /></Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}><InputField label="Intake Location" value={intake.intakeLocation} onChange={(v) => setI('intakeLocation', v)} /></Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}><SelectField label="Client Recovery Status" value={intake.clientRecoveryStatus} onChange={(v) => setI('clientRecoveryStatus', v)} options={CLIENT_CONNECT_STATUSES} /></Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}><InputField label="VA File Number" value={intake.vaFileNumber} onChange={(v) => setI('vaFileNumber', v)} /></Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Autocomplete size="small" options={TEXAS_COUNTIES as unknown as string[]} value={caseRecord.county || null} onChange={() => {}} renderInput={(params) => <TextField {...params} label="County" />} renderOption={(props, option) => (<li {...props} key={option} style={{ color: '#000' }}>{option}</li>)} />
            </Grid>
          </Grid>
          <SaveBar onSave={save} />
        </CardContent>
      </Card>

      {/* 2. Client Information */}
      <Card variant="outlined">
        <CardContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="subtitle2" sx={{ pb: 1, mb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            Client information
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}><InputField label="First Name" value={clientForm.firstName} onChange={(v) => setC('firstName', v)} /></Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}><InputField label="Middle Name" value={clientForm.middleName} onChange={(v) => setC('middleName', v)} /></Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}><InputField label="Last Name" value={clientForm.lastName} onChange={(v) => setC('lastName', v)} /></Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}><InputField label="Nickname / Other Name" value={clientForm.nickname} onChange={(v) => setC('nickname', v)} /></Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}><InputField label="Date of Birth" type="date" value={clientForm.dateOfBirth} onChange={(v) => setC('dateOfBirth', v)} /></Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}><InputField label="Gender" value={clientForm.gender} onChange={(v) => setC('gender', v)} /></Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}><InputField label="Phone Number" value={clientForm.phoneHome} onChange={(v) => setC('phoneHome', v)} /></Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}><InputField label="Alternate Phone" value={clientForm.phoneAlternate} onChange={(v) => setC('phoneAlternate', v)} /></Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}><InputField label="Email" value={clientForm.email} onChange={(v) => setC('email', v)} /></Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}><InputField label="Co-applicant Name" value={clientForm.coApplicantName} onChange={(v) => setC('coApplicantName', v)} /></Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}><InputField label="Co-applicant Phone" value={clientForm.coApplicantPhone} onChange={(v) => setC('coApplicantPhone', v)} /></Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField select size="small" fullWidth label="Co-applicant Relationship" value={coAppRels}
                onChange={(e) => setCoAppRels(typeof e.target.value === 'string' ? e.target.value.split(',') : (e.target.value as unknown as string[]))}
                slotProps={{ select: { multiple: true, renderValue: (selected) => (selected as string[]).join(', ') } }}
              >
                {CO_APPLICANT_RELATIONSHIPS.map((r) => (
                  <MenuItem key={r} value={r}><Checkbox size="small" checked={coAppRels.includes(r)} />{r}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
          <Box sx={{ mt: 1 }}>
            <FormControlLabel control={<Checkbox checked={client?.headOfHousehold ?? false} />} label="Head of Household" />
          </Box>
          <SaveBar onSave={save} />
        </CardContent>
      </Card>

      {/* 3. Household Members */}
      <Card variant="outlined">
        <CardContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="subtitle2" sx={{ pb: 1, mb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            Household members
          </Typography>
          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
            <Button variant="contained" startIcon={<Plus />} onClick={openAddMember}>Add member</Button>
          </Stack>
          <TableContainer sx={{ maxHeight: 480 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '16%' }}>Name</TableCell>
                  <TableCell align="center" sx={{ width: '14%' }}>Relationship</TableCell>
                  <TableCell align="center" sx={{ width: '12%' }}>Date of birth</TableCell>
                  <TableCell align="center" sx={{ width: '8%' }}>Age</TableCell>
                  <TableCell align="center" sx={{ width: '12%' }}>Condition</TableCell>
                  <TableCell align="center" sx={{ width: '12%' }}>Race</TableCell>
                  <TableCell align="center" sx={{ width: '14%' }}>Ethnicity</TableCell>
                  <TableCell align="right" sx={{ width: '6%' }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {hhRows.map((m) => {
                  const age = m.dateOfBirth ? Math.floor((Date.now() - new Date(m.dateOfBirth).getTime()) / 31557600000) : null;
                  return (
                    <TableRow key={m.id}>
                      <TableCell align="center"><Typography variant="body2" sx={{ fontWeight: 600 }}>{m.firstName} {m.lastName}</Typography></TableCell>
                      <TableCell align="center">{m.relationship}</TableCell>
                      <TableCell align="center">{formatDob(m.dateOfBirth)}</TableCell>
                      <TableCell align="center">{age != null ? age : '—'}</TableCell>
                      <TableCell align="center">{m.condition}</TableCell>
                      <TableCell align="center">{m.race ?? '—'}</TableCell>
                      <TableCell align="center">{m.ethnicity ?? '—'}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEditMember(m)}>
                            <Pencil />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {hhRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" sx={{ color: (t) => t.palette.text.secondary }}>No household members recorded.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Household member add/edit dialog */}
      <Dialog open={hhOpen} onClose={() => setHhOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{hhDraft.id ? 'Edit household member' : 'Add household member'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12, sm: 6 }}><InputField label="First Name" value={hhDraft.firstName} onChange={(v) => setHhDraft((p) => ({ ...p, firstName: v }))} /></Grid>
            <Grid size={{ xs: 12, sm: 6 }}><InputField label="Last Name" value={hhDraft.lastName} onChange={(v) => setHhDraft((p) => ({ ...p, lastName: v }))} /></Grid>
            <Grid size={{ xs: 12, sm: 6 }}><SelectField label="Relationship" value={hhDraft.relationship} onChange={(v) => setHhDraft((p) => ({ ...p, relationship: v }))} options={HOUSEHOLD_RELATIONSHIPS} allowEmpty={false} /></Grid>
            <Grid size={{ xs: 12, sm: 6 }}><InputField label="Date of Birth" type="date" value={hhDraft.dateOfBirth} onChange={(v) => setHhDraft((p) => ({ ...p, dateOfBirth: v }))} /></Grid>
            <Grid size={{ xs: 12, sm: 6 }}><SelectField label="Condition" value={hhDraft.condition} onChange={(v) => setHhDraft((p) => ({ ...p, condition: v }))} options={HOUSEHOLD_CONDITIONS} allowEmpty={false} /></Grid>
            <Grid size={{ xs: 12, sm: 6 }}><SelectField label="Race" value={hhDraft.race} onChange={(v) => setHhDraft((p) => ({ ...p, race: v }))} options={RACE_OPTIONS} /></Grid>
            <Grid size={{ xs: 12, sm: 6 }}><SelectField label="Ethnicity" value={hhDraft.ethnicity} onChange={(v) => setHhDraft((p) => ({ ...p, ethnicity: v }))} options={ETHNICITY_OPTIONS} /></Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>Shelter Service Needs</Typography>
              <Box>
                {SHELTER_SERVICE_NEEDS.map((need) => (
                  <FormControlLabel key={need} control={<Checkbox size="small" checked={hhDraft.shelterServiceNeeds.includes(need)} onChange={() => toggleHhNeed(need)} />} label={need} />
                ))}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHhOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveHhMember} disabled={!hhDraft.firstName.trim() || !hhDraft.lastName.trim()}>
            {hhDraft.id ? 'Save changes' : 'Add member'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 4. Household Income */}
      <Card variant="outlined">
        <CardContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="subtitle2" sx={{ pb: 1, mb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            Household income
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}><InputField label="Gross Monthly Household Income" value={intake.grossMonthlyIncome} onChange={(v) => setI('grossMonthlyIncome', v)} /></Grid>
            <Grid size={{ xs: 12, sm: 6 }}><InputField label="Monthly Household Expenses" value={intake.monthlyExpenses} onChange={(v) => setI('monthlyExpenses', v)} /></Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Release and Consent — 2-column checkboxes */}
      <Card variant="outlined">
        <CardContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="subtitle2" sx={{ pb: 1, mb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            Release and consent
          </Typography>
          <Box>
            <FormControlLabel
              control={<Checkbox checked={releaseVerified} onChange={(e) => handleReleaseToggle(e.target.checked)} />}
              label="Release of Information verified"
            />
            {verifiedStamp && (
              <Typography variant="caption" sx={{ display: 'block', ml: 4, color: (t) => t.palette.success.main }}>{verifiedStamp}</Typography>
            )}
          </Box>
          <Grid container spacing={0}>
            {([
              ['femaReleaseUploaded', 'VA Release uploaded'],
              ['rightsResponsibilitiesUploaded', 'Rights & Responsibilities uploaded'],
              ['verbalConsentRecorded', 'Applicant verbal consent recorded'],
              ['authorizationToShare', 'Authorization to share'],
              ['ninetyDayNotice', '90-day notice'],
              ['organizationOptOut', 'Organization opt-out'],
              ['revokingConsent', 'Revoking consent'],
            ] as [keyof typeof consent, string][]).map(([key, label]) => (
              <Grid key={key} size={{ xs: 12, sm: 6 }}>
                <FormControlLabel control={<Checkbox checked={consent[key]} onChange={(e) => setConsentField(key, e.target.checked)} />} label={label} />
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Signature section */}
      <Card variant="outlined">
        <CardContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="subtitle2" sx={{ pb: 1, mb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            Registrant signature
          </Typography>
          <SignaturePad />
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Verify Release of Information</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            I verify that the Release of Information has been received and reviewed. This action
            records who verified it and when.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={confirmRelease}>
            I verify
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Household tab — composition metrics + add/edit members
// ---------------------------------------------------------------------------

interface MemberDraft {
  id: string | null;
  firstName: string;
  lastName: string;
  relationship: string;
  dateOfBirth: string;
  condition: string;
  race: string;
  ethnicity: string;
  shelterServiceNeeds: string[];
}

const EMPTY_MEMBER: MemberDraft = {
  id: null,
  firstName: '',
  lastName: '',
  relationship: 'Self',
  dateOfBirth: '',
  condition: 'Not Affected',
  race: '',
  ethnicity: '',
  shelterServiceNeeds: [],
};

function HouseholdTab({
  household,
  clientId,
}: {
  household: TcrHouseholdMember[];
  clientId: string;
}) {
  const [rows, setRows] = useState<TcrHouseholdMember[]>(household);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<MemberDraft>(EMPTY_MEMBER);

  const composition = useMemo(
    () => computeComposition(rows.map((m) => m.dateOfBirth)),
    [rows],
  );

  const openAdd = () => {
    setDraft(EMPTY_MEMBER);
    setOpen(true);
  };
  const openEdit = (m: TcrHouseholdMember) => {
    setDraft({
      id: m.id,
      firstName: m.firstName,
      lastName: m.lastName,
      relationship: m.relationship,
      dateOfBirth: m.dateOfBirth ?? '',
      condition: m.condition,
      race: m.race ?? '',
      ethnicity: m.ethnicity ?? '',
      shelterServiceNeeds: [...m.shelterServiceNeeds],
    });
    setOpen(true);
  };

  const toggleNeed = (need: string) =>
    setDraft((p) => ({
      ...p,
      shelterServiceNeeds: p.shelterServiceNeeds.includes(need)
        ? p.shelterServiceNeeds.filter((n) => n !== need)
        : [...p.shelterServiceNeeds, need],
    }));

  const handleSave = () => {
    const now = new Date().toISOString();
    if (draft.id) {
      setRows((prev) =>
        prev.map((m) =>
          m.id === draft.id
            ? {
                ...m,
                firstName: draft.firstName,
                lastName: draft.lastName,
                relationship: draft.relationship,
                dateOfBirth: draft.dateOfBirth || null,
                condition: draft.condition,
                race: draft.race || null,
                ethnicity: draft.ethnicity || null,
                shelterServiceNeeds: draft.shelterServiceNeeds,
                updatedAt: now,
              }
            : m,
        ),
      );
    } else {
      setRows((prev) => [
        ...prev,
        {
          id: `hh-${Date.now()}`,
          clientId,
          firstName: draft.firstName,
          lastName: draft.lastName,
          relationship: draft.relationship,
          dateOfBirth: draft.dateOfBirth || null,
          condition: draft.condition,
          race: draft.race || null,
          ethnicity: draft.ethnicity || null,
          shelterServiceNeeds: draft.shelterServiceNeeds,
          createdAt: now,
          updatedAt: now,
        },
      ]);
    }
    setOpen(false);
  };

  return (
    <Stack spacing={2}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <MetricMiniCard title="Adults" value={composition.adults} />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <MetricMiniCard title="Children" value={composition.children} />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <MetricMiniCard title="Seniors (65+)" value={composition.seniors} />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <MetricMiniCard title="Total" value={composition.total} />
        </Grid>
      </Grid>

      <Stack direction="row" justifyContent="flex-end">
        <Button variant="contained" startIcon={<Plus />} onClick={openAdd}>
          Add member
        </Button>
      </Stack>

      <TableContainer sx={{ maxHeight: 480 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '16%' }}>Name</TableCell>
              <TableCell align="center" sx={{ width: '16%' }}>Relationship</TableCell>
              <TableCell align="center" sx={{ width: '14%' }}>Date of birth</TableCell>
              <TableCell align="center" sx={{ width: '14%' }}>Condition</TableCell>
              <TableCell align="center" sx={{ width: '14%' }}>Race</TableCell>
              <TableCell align="center" sx={{ width: '16%' }}>Ethnicity</TableCell>
              <TableCell align="right" sx={{ width: '6%' }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((m) => (
              <TableRow key={m.id}>
                <TableCell align="center"><Typography variant="body2" sx={{ fontWeight: 600 }}>{m.firstName} {m.lastName}</Typography></TableCell>
                <TableCell align="center">{m.relationship}</TableCell>
                <TableCell align="center">{formatDob(m.dateOfBirth)}</TableCell>
                <TableCell align="center">{m.condition}</TableCell>
                <TableCell align="center">{m.race ?? '—'}</TableCell>
                <TableCell align="center">{m.ethnicity ?? '—'}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => openEdit(m)}>
                      <Pencil />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" sx={{ color: (t) => t.palette.text.secondary }}>
                    No household members recorded.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{draft.id ? 'Edit household member' : 'Add household member'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <InputField
                label="First Name"
                value={draft.firstName}
                onChange={(v) => setDraft((p) => ({ ...p, firstName: v }))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <InputField
                label="Last Name"
                value={draft.lastName}
                onChange={(v) => setDraft((p) => ({ ...p, lastName: v }))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <SelectField
                label="Relationship"
                value={draft.relationship}
                onChange={(v) => setDraft((p) => ({ ...p, relationship: v }))}
                options={HOUSEHOLD_RELATIONSHIPS}
                allowEmpty={false}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <InputField
                label="Date of Birth"
                type="date"
                value={draft.dateOfBirth}
                onChange={(v) => setDraft((p) => ({ ...p, dateOfBirth: v }))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <SelectField
                label="Condition"
                value={draft.condition}
                onChange={(v) => setDraft((p) => ({ ...p, condition: v }))}
                options={HOUSEHOLD_CONDITIONS}
                allowEmpty={false}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <SelectField
                label="Race"
                value={draft.race}
                onChange={(v) => setDraft((p) => ({ ...p, race: v }))}
                options={RACE_OPTIONS}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <SelectField
                label="Ethnicity"
                value={draft.ethnicity}
                onChange={(v) => setDraft((p) => ({ ...p, ethnicity: v }))}
                options={ETHNICITY_OPTIONS}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>
                Shelter Service Needs
              </Typography>
              <Box>
                {SHELTER_SERVICE_NEEDS.map((need) => (
                  <FormControlLabel
                    key={need}
                    control={
                      <Checkbox
                        size="small"
                        checked={draft.shelterServiceNeeds.includes(need)}
                        onChange={() => toggleNeed(need)}
                      />
                    }
                    label={need}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!draft.firstName.trim() || !draft.lastName.trim()}
          >
            {draft.id ? 'Save changes' : 'Add member'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Assessment tab — vulnerability flags + language/risk + impacts
// ---------------------------------------------------------------------------

function AssessmentTab({ client }: { client: TcrClient | null }) {
  const save = useMockSave();
  const [vuln, setVuln] = useState({
    headOfHousehold65OrOlder: client?.headOfHousehold65OrOlder ?? false,
    accessOrFunctionalNeeds: client?.accessOrFunctionalNeeds ?? false,
    unsafeUnsanitaryHousing: client?.unsafeUnsanitaryHousing ?? false,
    persistentSocialServiceClient: client?.persistentSocialServiceClient ?? false,
    significantEmotionalDistress: client?.significantEmotionalDistress ?? false,
    singleHeadWithMinors: client?.singleHeadWithMinors ?? false,
  });
  const set = (k: keyof typeof vuln, v: boolean) => setVuln((p) => ({ ...p, [k]: v }));
  const [notes, setNotes] = useState(client?.serviceNeedsNotes ?? '');

  if (!client) return <Typography>No client data available.</Typography>;

  const rows: [keyof typeof vuln, string][] = [
    ['headOfHousehold65OrOlder', 'Head of Household 65 or Older'],
    ['accessOrFunctionalNeeds', 'Access or Functional Needs'],
    ['unsafeUnsanitaryHousing', 'Unsafe/Unsanitary Housing'],
    ['persistentSocialServiceClient', 'Persistent Social Service Client'],
    ['significantEmotionalDistress', 'Significant Emotional Distress'],
    ['singleHeadWithMinors', 'Single Head of Household with Minors'],
  ];

  return (
    <Stack spacing={2}>
      <Card variant="outlined">
        <CardContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="subtitle2" sx={{ pb: 1, mb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            Vulnerability flags
          </Typography>
          <Grid container spacing={0}>
            {rows.map(([key, label]) => (
              <Grid key={key} size={{ xs: 12, sm: 6 }}>
                <FormControlLabel control={<Checkbox checked={vuln[key]} onChange={(e) => set(key, e.target.checked)} />} label={label} />
              </Grid>
            ))}
          </Grid>
          <SaveBar onSave={save} />
        </CardContent>
      </Card>
      <Card variant="outlined">
        <CardContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="subtitle2" sx={{ pb: 1, mb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            Recovery needs &amp; notes
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}><LabelValue label="Total Household Members" value={client.totalHouseholdMembers} /></Grid>
            <Grid size={{ xs: 12, sm: 6 }}><LabelValue label="Client Connect Status" value={client.clientConnectStatus} /></Grid>
          </Grid>
          <Box sx={{ mt: 2, width: '100%' }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: (t) => t.palette.text.secondary, display: 'block', mb: 0.5 }}>
              Recovery Needs Notes
            </Typography>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={6}
              style={{ width: '100%', padding: '8px 12px', fontSize: 14, fontFamily: 'inherit', borderRadius: 4, border: '1px solid #ccc', resize: 'vertical', boxSizing: 'border-box' }}
            />
          </Box>
          <SaveBar onSave={save} />
        </CardContent>
      </Card>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Housing tab — editable prior/current housing forms + jump nav + work orders
// ---------------------------------------------------------------------------

const CURRENT_RESIDENCE_OPTIONS = ['Same as prior address', 'Temporary housing', 'Shelter', 'Other'] as const;
const SAME_AS_PRE = 'Same as prior address';

function HousingTab({ caseRecord, client }: { caseRecord: TcrCase; client: TcrClient | null }) {
  const save = useMockSave();
  const addr = client?.primaryAddress;

  const [form, setForm] = useState({
    priorLivingSituation: caseRecord.priorLivingSituation ?? '',
    housingType: caseRecord.housingType ?? '',
    addressVerificationObtained: caseRecord.addressVerificationObtained ?? '',
    levelOfDamage: caseRecord.housingCondition ?? '',
    numberOfFloors: caseRecord.numberOfFloors ?? '',
    squareFootage: caseRecord.squareFootage ?? '',
    basement: caseRecord.basement ?? '',
    permanentResidenceStatus: caseRecord.permanentResidenceStatus ?? '',
    ownerRenter: caseRecord.ownerRenter ?? '',
    waterDepth: caseRecord.waterDepth ?? '',
    totalEstDamages: caseRecord.totalEstDamages ?? '',
    estInsCoverage: caseRecord.estInsCoverage ?? '',
    estUninsuredDamages: caseRecord.estUninsuredDamages ?? '',
    insDeductible: caseRecord.insDeductible ?? '',
    damageReported: caseRecord.damageReported ?? '',
    reportedAgency: caseRecord.reportedAgency ?? '',
    cleanupNeed: caseRecord.cleanupNeed ?? '',
    cleanupNeedType: caseRecord.cleanupNeedType ?? '',
    damageNotes: caseRecord.damageNotes ?? '',
    currentResidence: caseRecord.currentResidence ?? '',
    shelterStatus: caseRecord.shelterStatus ?? '',
    shelterName: caseRecord.shelterName ?? '',
    currentResidenceNotes: caseRecord.currentResidenceNotes ?? '',
    temporaryHousingStatus: caseRecord.temporaryHousingStatus ?? '',
    temporaryHousingStartDate: caseRecord.temporaryHousingStartDate ?? '',
    temporaryHousingEndDate: caseRecord.temporaryHousingEndDate ?? '',
    housingNotes: caseRecord.housingNotes ?? '',
    // Post-disaster address — starts blank, auto-fills on "Same as pre-disaster"
    postAddress: '',
    postCity: '',
    postCounty: '',
    postState: '',
    postZip: '',
  });
  const set = (k: keyof typeof form, v: string) => setForm((p) => ({ ...p, [k]: v }));

  const handleCurrentResidenceChange = (val: string) => {
    set('currentResidence', val);
    if (val === SAME_AS_PRE && addr) {
      setForm((p) => ({
        ...p,
        currentResidence: val,
        postAddress: addr.address1 ?? '',
        postCity: addr.city ?? '',
        postCounty: addr.county ?? caseRecord.county ?? '',
        postState: addr.state ?? '',
        postZip: addr.zip ?? '',
      }));
    } else {
      setForm((p) => ({
        ...p,
        currentResidence: val,
        postAddress: '',
        postCity: '',
        postCounty: '',
        postState: '',
        postZip: '',
      }));
    }
  };

  const preRef = useRef<HTMLDivElement>(null);
  const postRef = useRef<HTMLDivElement>(null);
  const woRef = useRef<HTMLDivElement>(null);
  const scrollTo = (ref: React.RefObject<HTMLDivElement | null>) =>
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const showPostDetails = form.currentResidence !== SAME_AS_PRE && form.currentResidence !== '';

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button size="small" variant="outlined" onClick={() => scrollTo(preRef)}>
          Prior Living Situation
        </Button>
        <Button size="small" variant="outlined" onClick={() => scrollTo(postRef)}>
          Current Housing
        </Button>
        <Button size="small" variant="outlined" onClick={() => scrollTo(woRef)}>
          Work orders
        </Button>
      </Stack>

      {/* Prior Living Situation housing — full width, multi-column fields */}
      <Card variant="outlined" ref={preRef}>
        <CardContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="subtitle2" sx={{ pb: 1, mb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            Prior Housing
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}><InputField label="Living Situation" value={form.priorLivingSituation} onChange={(v) => set('priorLivingSituation', v)} /></Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}><InputField label="Housing Type" value={form.housingType} onChange={(v) => set('housingType', v)} /></Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}><SelectField label="Owner / Renter" value={form.ownerRenter} onChange={(v) => set('ownerRenter', v)} options={OWNER_RENTER_OPTIONS} /></Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}><SelectField label="Address Verification" value={form.addressVerificationObtained} onChange={(v) => set('addressVerificationObtained', v)} options={['Yes', 'No'] as const} /></Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}><InputField label="Address" value={addr?.address1 ?? ''} onChange={() => {}} /></Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}><InputField label="Unit / Suite" value={addr?.address2 ?? ''} onChange={() => {}} /></Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}><InputField label="City" value={addr?.city ?? ''} onChange={() => {}} /></Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <Autocomplete size="small" options={TEXAS_COUNTIES as unknown as string[]} value={addr?.county ?? caseRecord.county ?? null} onChange={() => {}} renderInput={(params) => <TextField {...params} label="County" />} renderOption={(props, option) => (<li {...props} key={option} style={{ color: '#000' }}>{option}</li>)} />
            </Grid>
            <Grid size={{ xs: 6, md: 1 }}><InputField label="State" value={addr?.state ?? ''} onChange={() => {}} /></Grid>
            <Grid size={{ xs: 6, md: 1 }}><InputField label="ZIP" value={addr?.zip ?? ''} onChange={() => {}} /></Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}><SelectField label="Housing Condition" value={form.levelOfDamage} onChange={(v) => set('levelOfDamage', v)} options={DAMAGE_LEVELS} /></Grid>
            <Grid size={{ xs: 6, md: 2 }}><InputField label="Floors" value={form.numberOfFloors} onChange={(v) => set('numberOfFloors', v)} /></Grid>
            <Grid size={{ xs: 6, md: 2 }}><InputField label="Sq. Footage" value={form.squareFootage} onChange={(v) => set('squareFootage', v)} /></Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}><SelectField label="Basement" value={form.basement} onChange={(v) => set('basement', v)} options={['Yes', 'No'] as const} /></Grid>
          </Grid>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 6, md: 3 }}><InputField label="Est. Total Damages ($)" value={form.totalEstDamages} onChange={(v) => set('totalEstDamages', v)} /></Grid>
            <Grid size={{ xs: 6, md: 3 }}><InputField label="Est. Insurance Coverage ($)" value={form.estInsCoverage} onChange={(v) => set('estInsCoverage', v)} /></Grid>
            <Grid size={{ xs: 6, md: 3 }}><InputField label="Est. Uninsured Damages ($)" value={form.estUninsuredDamages} onChange={(v) => set('estUninsuredDamages', v)} /></Grid>
            <Grid size={{ xs: 6, md: 3 }}><InputField label="Insurance Deductible ($)" value={form.insDeductible} onChange={(v) => set('insDeductible', v)} /></Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}><SelectField label="Permanent Residence Status" value={form.permanentResidenceStatus} onChange={(v) => set('permanentResidenceStatus', v)} options={PERMANENT_RESIDENCE_STATUSES} /></Grid>
          </Grid>
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: (th) => th.palette.text.secondary, display: 'block', mb: 0.5 }}>Housing Notes</Typography>
            <textarea value={form.damageNotes} onChange={(e) => set('damageNotes', e.target.value)} rows={3} style={{ width: '100%', padding: '8px 12px', fontSize: 14, fontFamily: 'inherit', borderRadius: 4, border: '1px solid #ccc', resize: 'vertical', boxSizing: 'border-box' }} />
          </Box>
          <SaveBar onSave={save} />
        </CardContent>
      </Card>

      {/* Current housing — full width */}
      <Card variant="outlined" ref={postRef}>
        <CardContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="subtitle2" sx={{ pb: 1, mb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            Current Housing
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <SelectField label="Current Residence" value={form.currentResidence} onChange={handleCurrentResidenceChange} options={CURRENT_RESIDENCE_OPTIONS} />
            </Grid>
            {form.currentResidence === 'Shelter' && (
              <>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <SelectField label="Shelter Status" value={form.shelterStatus} onChange={(v) => set('shelterStatus', v)} options={SHELTER_STATUSES} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <InputField label="Shelter Name" value={form.shelterName} onChange={(v) => set('shelterName', v)} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <InputField label="Shelter Notes" value={form.currentResidenceNotes} onChange={(v) => set('currentResidenceNotes', v)} />
                </Grid>
              </>
            )}
            {form.currentResidence === 'Other' && (
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <InputField label="Other — Describe" value={form.currentResidenceNotes} onChange={(v) => set('currentResidenceNotes', v)} />
              </Grid>
            )}
            {form.currentResidence === 'Temporary housing' && (
              <>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}><SelectField label="Temporary Housing Type" value={form.temporaryHousingStatus} onChange={(v) => set('temporaryHousingStatus', v)} options={TEMP_HOUSING_STATUSES} /></Grid>
                <Grid size={{ xs: 6, md: 3 }}><InputField label="Start Date" type="date" value={form.temporaryHousingStartDate} onChange={(v) => set('temporaryHousingStartDate', v)} /></Grid>
                <Grid size={{ xs: 6, md: 3 }}><InputField label="End Date" type="date" value={form.temporaryHousingEndDate} onChange={(v) => set('temporaryHousingEndDate', v)} /></Grid>
              </>
            )}
            {showPostDetails && (
              <>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}><InputField label="Address" value={form.postAddress} onChange={(v) => set('postAddress', v)} /></Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2 }}><InputField label="City" value={form.postCity} onChange={(v) => set('postCity', v)} /></Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                  <Autocomplete size="small" options={TEXAS_COUNTIES as unknown as string[]} value={form.postCounty || null} onChange={(_e, v) => set('postCounty', v ?? '')} renderInput={(params) => <TextField {...params} label="County" />} renderOption={(props, option) => (<li {...props} key={option} style={{ color: '#000' }}>{option}</li>)} />
                </Grid>
                <Grid size={{ xs: 6, md: 2 }}><InputField label="State" value={form.postState} onChange={(v) => set('postState', v)} /></Grid>
                <Grid size={{ xs: 6, md: 2 }}><InputField label="ZIP" value={form.postZip} onChange={(v) => set('postZip', v)} /></Grid>
              </>
            )}
          </Grid>
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: (th) => th.palette.text.secondary, display: 'block', mb: 0.5 }}>Housing Notes</Typography>
            <textarea value={form.housingNotes} onChange={(e) => set('housingNotes', e.target.value)} rows={3} style={{ width: '100%', padding: '8px 12px', fontSize: 14, fontFamily: 'inherit', borderRadius: 4, border: '1px solid #ccc', resize: 'vertical', boxSizing: 'border-box' }} />
          </Box>
          <SaveBar onSave={save} />
        </CardContent>
      </Card>

      {/* Work Orders */}
      <Card variant="outlined" ref={woRef}>
        <CardContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <WorkOrdersPanel caseId={caseRecord.caseId} />
        </CardContent>
      </Card>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Insurance tab — add / edit / remove policies
// ---------------------------------------------------------------------------

interface InsuranceDraft {
  id: string | null;
  coverageType: string;
  company: string;
  claimStatus: string;
  notes: string;
}

const EMPTY_INSURANCE: InsuranceDraft = {
  id: null,
  coverageType: 'Homeowners',
  company: '',
  claimStatus: '',
  notes: '',
};

function InsuranceTab({ caseRecord }: { caseRecord: TcrCase }) {
  const save = useMockSave();
  const [adequacy, setAdequacy] = useState(caseRecord.insuranceAdequacy ?? '');
  const [deductibleRange, setDeductibleRange] = useState(caseRecord.insuranceDeductibleRange ?? '');
  const [rows, setRows] = useState<InsuranceEntry[]>(caseRecord.insuranceEntries);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<InsuranceDraft>(EMPTY_INSURANCE);

  const openAdd = () => {
    setDraft(EMPTY_INSURANCE);
    setOpen(true);
  };
  const openEdit = (entry: InsuranceEntry) => {
    setDraft({
      id: entry.id,
      coverageType: entry.coverageType,
      company: entry.company ?? '',
      claimStatus: entry.claimStatus ?? '',
      notes: entry.notes ?? '',
    });
    setOpen(true);
  };
  const remove = (id: string) => setRows((prev) => prev.filter((e) => e.id !== id));

  const handleSave = () => {
    if (draft.id) {
      setRows((prev) =>
        prev.map((e) =>
          e.id === draft.id
            ? {
                ...e,
                coverageType: draft.coverageType,
                company: draft.company || null,
                claimStatus: draft.claimStatus || null,
                notes: draft.notes || null,
              }
            : e,
        ),
      );
    } else {
      setRows((prev) => [
        ...prev,
        {
          id: `ins-${Date.now()}`,
          coverageType: draft.coverageType,
          company: draft.company || null,
          claimStatus: draft.claimStatus || null,
          notes: draft.notes || null,
        },
      ]);
    }
    setOpen(false);
  };

  return (
    <Stack spacing={2}>
      {/* Insurance assessment */}
      <Card variant="outlined">
        <CardContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="subtitle2" sx={{ pb: 1, mb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            Insurance Assessment
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <SelectField label="How would you describe your insurance coverage?" value={adequacy} onChange={(v) => setAdequacy(v)} options={INSURANCE_ADEQUACY_OPTIONS} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <SelectField label="What is your insurance deductible?" value={deductibleRange} onChange={(v) => setDeductibleRange(v)} options={INSURANCE_DEDUCTIBLE_RANGES} />
            </Grid>
          </Grid>
          <SaveBar onSave={save} />
        </CardContent>
      </Card>

      {/* Insurance policies */}
      <Card variant="outlined">
        <CardContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ pb: 1, mb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2">Insurance Policies</Typography>
            <Button size="small" variant="contained" startIcon={<Plus />} onClick={openAdd}>Add policy</Button>
          </Stack>
      <TableContainer sx={{ maxHeight: 480 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell align="center">Coverage Type</TableCell>
              <TableCell align="center">Company</TableCell>
              <TableCell align="center">Claim Status</TableCell>
              <TableCell align="center">Notes</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((entry) => (
              <TableRow key={entry.id}>
                <TableCell align="center">{entry.coverageType}</TableCell>
                <TableCell align="center">{entry.company ?? '—'}</TableCell>
                <TableCell align="center">
                  {entry.claimStatus ? <CaseStatusBadge status={entry.claimStatus} /> : '—'}
                </TableCell>
                <TableCell align="center">{entry.notes ?? '—'}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => openEdit(entry)}>
                      <Pencil />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remove">
                    <IconButton size="small" onClick={() => remove(entry.id)}>
                      <TrashCan />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography
                    variant="body2"
                    sx={{ color: (t) => t.palette.text.secondary, textAlign: 'center' }}
                  >
                    No insurance entries recorded.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{draft.id ? 'Edit policy' : 'Add policy'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <SelectField
              label="Coverage Type"
              value={draft.coverageType}
              onChange={(v) => setDraft((p) => ({ ...p, coverageType: v }))}
              options={INSURANCE_TYPES}
              allowEmpty={false}
            />
            <InputField
              label="Company"
              value={draft.company}
              onChange={(v) => setDraft((p) => ({ ...p, company: v }))}
            />
            <SelectField
              label="Claim Status"
              value={draft.claimStatus}
              onChange={(v) => setDraft((p) => ({ ...p, claimStatus: v }))}
              options={CLAIM_STATUSES}
            />
            <InputField
              label="Notes"
              value={draft.notes}
              onChange={(v) => setDraft((p) => ({ ...p, notes: v }))}
              multiline
              rows={4}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            {draft.id ? 'Save changes' : 'Add policy'}
          </Button>
        </DialogActions>
      </Dialog>
      </CardContent>
    </Card>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Assistance tab — editable VA Claim / Education Benefits / Partner Referral forms
// ---------------------------------------------------------------------------

function AssistanceTab({ caseRecord }: { caseRecord: TcrCase }) {
  const save = useMockSave();
  const [form, setForm] = useState({
    femaApplicationStatus: caseRecord.femaApplicationStatus ?? '',
    femaInitialDeterminationDate: caseRecord.femaInitialDeterminationDate ?? '',
    femaTotalAmountReceived: caseRecord.femaTotalAmountReceived ?? '',
    femaAppealStatus: caseRecord.femaAppealStatus ?? '',
    femaInitialAppealDate: caseRecord.femaInitialAppealDate ?? '',
    femaAppealApprovedAmount: caseRecord.femaAppealApprovedAmount ?? '',
    femaReasonForDenial: caseRecord.femaReasonForDenial ?? '',
    sbaLoanStatus: caseRecord.sbaLoanStatus ?? '',
    sbaTotalAmount: caseRecord.sbaTotalAmount ?? '',
    sbaResponseDate: caseRecord.sbaResponseDate ?? '',
    appliedForOtherLoans: caseRecord.appliedForOtherLoans ?? '',
    caseSentToLtrg: caseRecord.caseSentToLtrg ?? '',
    dateSentToLtrg: caseRecord.dateSentToLtrg ?? '',
    ltrgCaseStatus: caseRecord.ltrgCaseStatus ?? '',
    ltrgAwardValue: caseRecord.ltrgAwardValue ?? '',
  });
  const set = (k: keyof typeof form, v: string) => setForm((p) => ({ ...p, [k]: v }));

  return (
    <Stack spacing={2}>
      {/* VA Claim & Benefits — 3-column grid */}
      <Card variant="outlined">
        <CardContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="subtitle2" sx={{ pb: 1, mb: 1.5, borderBottom: 1, borderColor: 'divider' }}>VA Claim &amp; Benefits</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}><SelectField label="Claim Status" value={form.femaApplicationStatus} onChange={(v) => set('femaApplicationStatus', v)} options={FEMA_STATUSES} /></Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}><InputField label="Initial Determination Date" type="date" value={form.femaInitialDeterminationDate} onChange={(v) => set('femaInitialDeterminationDate', v)} /></Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}><InputField label="Total Amount Received" value={form.femaTotalAmountReceived} onChange={(v) => set('femaTotalAmountReceived', v)} /></Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}><SelectField label="Appeal Status" value={form.femaAppealStatus} onChange={(v) => set('femaAppealStatus', v)} options={APPEAL_STATUSES} /></Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}><InputField label="Appeal Date" type="date" value={form.femaInitialAppealDate} onChange={(v) => set('femaInitialAppealDate', v)} /></Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}><InputField label="Appeal Approved Amount" value={form.femaAppealApprovedAmount} onChange={(v) => set('femaAppealApprovedAmount', v)} /></Grid>
          </Grid>
          <Box sx={{ mt: 2, width: '100%' }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: (t) => t.palette.text.secondary, display: 'block', mb: 0.5 }}>
              Reason for Denial
            </Typography>
            <textarea
              value={form.femaReasonForDenial}
              onChange={(e) => set('femaReasonForDenial', e.target.value)}
              rows={4}
              style={{ width: '100%', padding: '8px 12px', fontSize: 14, fontFamily: 'inherit', borderRadius: 4, border: '1px solid #ccc', resize: 'vertical', boxSizing: 'border-box' }}
            />
          </Box>
          <SaveBar onSave={save} />
        </CardContent>
      </Card>

      {/* Education Benefits — 3-column grid */}
      <Card variant="outlined">
        <CardContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="subtitle2" sx={{ pb: 1, mb: 1.5, borderBottom: 1, borderColor: 'divider' }}>Education Benefits</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}><SelectField label="Benefit Status" value={form.sbaLoanStatus} onChange={(v) => set('sbaLoanStatus', v)} options={SBA_STATUSES} /></Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}><InputField label="Total Amount" value={form.sbaTotalAmount} onChange={(v) => set('sbaTotalAmount', v)} /></Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}><InputField label="Response Date" type="date" value={form.sbaResponseDate} onChange={(v) => set('sbaResponseDate', v)} /></Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}><InputField label="Applied for Other Loans" value={form.appliedForOtherLoans} onChange={(v) => set('appliedForOtherLoans', v)} /></Grid>
          </Grid>
          <SaveBar onSave={save} />
        </CardContent>
      </Card>

      {/* Partner Referral — 3-column grid */}
      <Card variant="outlined">
        <CardContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="subtitle2" sx={{ pb: 1, mb: 1.5, borderBottom: 1, borderColor: 'divider' }}>Partner Referral</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}><InputField label="Case Sent to Partner" value={form.caseSentToLtrg} onChange={(v) => set('caseSentToLtrg', v)} /></Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}><InputField label="Date Sent to Partner" type="date" value={form.dateSentToLtrg} onChange={(v) => set('dateSentToLtrg', v)} /></Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}><SelectField label="Partner Case Status" value={form.ltrgCaseStatus} onChange={(v) => set('ltrgCaseStatus', v)} options={LTRG_STATUSES} /></Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}><InputField label="Partner Award Value" value={form.ltrgAwardValue} onChange={(v) => set('ltrgAwardValue', v)} /></Grid>
          </Grid>
          <SaveBar onSave={save} />
        </CardContent>
      </Card>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Recovery Plan tab — goal CRUD + expand + notes
// ---------------------------------------------------------------------------

function DaysRemainingBanner({
  planStartDate,
  planEndDate,
  totalDays,
}: {
  planStartDate: string;
  planEndDate: string;
  totalDays: number;
}) {
  const today = Date.now();
  const startMs = new Date(`${planStartDate}T00:00:00`).getTime();
  const endMs = new Date(`${planEndDate}T00:00:00`).getTime();
  const elapsed = Math.round((today - startMs) / MS_PER_DAY);
  const remaining = Math.round((endMs - today) / MS_PER_DAY);
  const pct = Math.min(100, Math.max(0, Math.round((elapsed / totalDays) * 100)));
  const expired = remaining < 0;
  const endFormatted = new Date(`${planEndDate}T00:00:00`).toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric',
  });
  const progressColor: 'primary' | 'warning' | 'error' = expired
    ? 'error'
    : remaining <= 14
      ? 'warning'
      : 'primary';

  return (
    <Box sx={{ mt: 1.5 }}>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="caption" sx={{ color: `${progressColor}.main`, fontWeight: 600 }}>
          {expired
            ? `Plan expired ${Math.abs(remaining)} day${Math.abs(remaining) !== 1 ? 's' : ''} ago`
            : `${remaining} day${remaining !== 1 ? 's' : ''} remaining`}
        </Typography>
        <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>
          Plan ends {endFormatted}
        </Typography>
      </Stack>
      <LinearProgress variant="determinate" value={pct} color={progressColor} sx={{ borderRadius: 1, height: 6 }} />
    </Box>
  );
}

const GOAL_STATUS_COLORS: Record<string, string> = {
  'Goal Met':                   '#2e7d32', // green
  'In Progress':                '#1565c0', // blue
  'Goal Partially Met':         '#f57c00', // amber
  'Goal Unmet':                 '#c62828', // red
  'Goal Abandoned':             '#616161', // gray
  'No Resources to Meet Goal':  '#6a1b9a', // purple
};

interface GanttEntry {
  label: string;
  fullName: string;
  status: string;
  offset: number;
  duration: number;
  color: string;
  noDate: boolean;
}

function RecoveryGanttChart({
  rows,
  planStartDate,
  totalDays,
}: {
  rows: TcrGoal[];
  planStartDate: string;
  totalDays: number;
}) {
  const planStartMs = new Date(`${planStartDate}T00:00:00`).getTime();

  const data: GanttEntry[] = rows.map((goal) => {
    const startDate = goal.startDate ?? null;
    const deadlineDate = goal.deadlineDate ?? null;
    const startDay = startDate
      ? Math.round((new Date(`${startDate}T00:00:00`).getTime() - planStartMs) / MS_PER_DAY)
      : 0;
    const endDay = deadlineDate
      ? Math.round((new Date(`${deadlineDate}T00:00:00`).getTime() - planStartMs) / MS_PER_DAY)
      : totalDays;
    const noDate = !startDate && !deadlineDate;
    return {
      label: goal.name,
      fullName: goal.name,
      status: goal.status,
      offset: Math.max(0, startDay),
      duration: Math.max(1, endDay - Math.max(0, startDay)),
      color: GOAL_STATUS_COLORS[goal.status] ?? '#1976d2',
      noDate,
    };
  });

  const formatTick = (dayOffset: number) => {
    const d = new Date(planStartMs + dayOffset * MS_PER_DAY);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const WRAP_WIDTH = 22;
  const wrapLabel = (text: string): string[] => {
    const words = text.split(' ');
    const lines: string[] = [];
    let current = '';
    for (const word of words) {
      const next = current ? `${current} ${word}` : word;
      if (next.length > WRAP_WIDTH && current) {
        lines.push(current);
        current = word;
      } else {
        current = next;
      }
    }
    if (current) lines.push(current);
    return lines;
  };

  const WrappedYAxisTick = ({ x, y, payload }: { x?: number; y?: number; payload?: { value: string } }) => {
    const lines = wrapLabel(payload?.value ?? '');
    const lineHeight = 14;
    const totalHeight = lines.length * lineHeight;
    return (
      <g transform={`translate(${x ?? 0},${y ?? 0})`}>
        {lines.map((line, i) => (
          <text
            key={i}
            x={-6}
            y={(i - (lines.length - 1) / 2) * lineHeight}
            dy={totalHeight > lineHeight ? 0 : 4}
            textAnchor="end"
            fontSize={12}
            fill="#333"
          >
            {line}
          </text>
        ))}
      </g>
    );
  };

  const barHeight = 36;
  const chartHeight = Math.max(100, rows.length * (barHeight + 14) + 70);

  return (
    <ResponsiveContainer width="100%" height={chartHeight}>
      <BarChart data={data} layout="vertical" margin={{ top: 8, right: 70, left: 0, bottom: 24 }} barSize={barHeight}>
        <XAxis type="number" domain={[0, totalDays]} tickFormatter={formatTick} tick={{ fontSize: 11 }} tickCount={7} />
        <YAxis type="category" dataKey="label" width={200} tick={<WrappedYAxisTick />} />
        <RechartsTooltip
          cursor={{ fill: 'rgba(0,0,0,0.04)' }}
          content={({ active, payload }) => {
            if (!active || !payload?.length) return null;
            const row = payload[0]?.payload as GanttEntry | undefined;
            if (!row) return null;
            return (
              <Box sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider', p: 1.5, borderRadius: 1, minWidth: 180 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>{row.fullName}</Typography>
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>Status: {row.status}</Typography>
                {row.noDate && (
                  <Typography variant="caption" sx={{ display: 'block', color: 'warning.main', mt: 0.5 }}>No goal dates set</Typography>
                )}
              </Box>
            );
          }}
        />
        <Bar dataKey="offset" stackId="g" fill="transparent" isAnimationActive={false} />
        <Bar dataKey="duration" stackId="g" isAnimationActive={false} radius={3}>
          {data.map((entry, idx) => (
            <Cell key={idx} fill={entry.color} opacity={entry.noDate ? 0.4 : 1} />
          ))}
          <LabelList
            dataKey="duration"
            content={(props) => {
              const { x, y, width, height, index } = props as {
                x: number; y: number; width: number; height: number; index: number;
              };
              const entry = data[index];
              if (!entry?.noDate || (width ?? 0) < 80) return null;
              return (
                <text
                  x={x + (width ?? 0) / 2}
                  y={y + (height ?? 0) / 2}
                  textAnchor="middle"
                  dominantBaseline="central"
                  style={{ fontSize: 11, fill: '#fff', fontWeight: 500, pointerEvents: 'none' }}
                >
                  No goal dates
                </text>
              );
            }}
          />
        </Bar>
        <ReferenceLine
          x={totalDays}
          stroke="#d32f2f"
          strokeWidth={2}
          strokeDasharray="5 3"
          label={{ value: 'Plan end', position: 'insideTopRight', fontSize: 11, fill: '#d32f2f' }}
        />
      </BarChart>
    </ResponsiveContainer>
  );
}

interface GoalDraft {
  id: string | null;
  name: string;
  recoveryNeed: string;
  startDate: string;
  deadlineDate: string;
  status: string;
}

const EMPTY_GOAL: GoalDraft = {
  id: null,
  name: '',
  recoveryNeed: '',
  startDate: '',
  deadlineDate: '',
  status: 'In Progress',
};

function RecoveryPlanTab({
  caseRecord,
  goals,
  clientId,
}: {
  caseRecord: TcrCase;
  goals: TcrGoal[];
  clientId: string;
}) {
  const save = useMockSave();
  const [rows, setRows] = useState<TcrGoal[]>(goals);
  const [plan, setPlan] = useState({
    recoveryPlanDate: caseRecord.recoveryPlanDate ?? '',
    recoveryTimeline: caseRecord.recoveryTimeline ?? '',
  });
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [goalOpen, setGoalOpen] = useState(false);
  const [draft, setDraft] = useState<GoalDraft>(EMPTY_GOAL);
  const [goalDateError, setGoalDateError] = useState<string | null>(null);
  const [noteFor, setNoteFor] = useState<TcrGoal | null>(null);
  const [noteText, setNoteText] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [planDateConfirmOpen, setPlanDateConfirmOpen] = useState(false);
  const [planDateDraft, setPlanDateDraft] = useState(caseRecord.recoveryPlanDate ?? '');

  const customEndMatch = plan.recoveryTimeline
    ? /^Custom \(ends (\d{4}-\d{2}-\d{2})\)$/.exec(plan.recoveryTimeline)
    : null;
  const planEndDate: string | null = customEndMatch
    ? (customEndMatch[1] ?? null)
    : plan.recoveryPlanDate && plan.recoveryTimeline
      ? (() => { const d = parseTimelineDays(plan.recoveryTimeline); return d !== null ? addDays(new Date(plan.recoveryPlanDate), d) : null; })()
      : null;
  const planTotalDays: number | null =
    planEndDate && plan.recoveryPlanDate
      ? Math.round((new Date(`${planEndDate}T00:00:00`).getTime() - new Date(`${plan.recoveryPlanDate}T00:00:00`).getTime()) / MS_PER_DAY)
      : null;

  const openAddGoal = () => {
    setDraft(EMPTY_GOAL);
    setGoalDateError(null);
    setGoalOpen(true);
  };
  const openEditGoal = (g: TcrGoal) => {
    setDraft({
      id: g.id,
      name: g.name,
      recoveryNeed: g.recoveryNeed,
      startDate: g.startDate ?? '',
      deadlineDate: g.deadlineDate ?? '',
      status: g.status,
    });
    setGoalDateError(null);
    setGoalOpen(true);
  };

  const saveGoal = () => {
    setGoalDateError(null);

    if (draft.startDate && draft.deadlineDate && draft.deadlineDate < draft.startDate) {
      setGoalDateError('Goal deadline cannot be earlier than the goal start date.');
      return;
    }

    if (planEndDate) {
      const formatted = new Date(`${planEndDate}T00:00:00`).toLocaleDateString('en-US', {
        month: 'long', day: 'numeric', year: 'numeric',
      });
      if (draft.startDate && draft.startDate > planEndDate) {
        setGoalDateError(
          `Goal start date exceeds the recovery plan end date (${formatted}). Please extend the recovery timeline or choose an earlier start date.`,
        );
        return;
      }
      if (draft.deadlineDate && draft.deadlineDate > planEndDate) {
        setGoalDateError(
          `Goal deadline exceeds the recovery plan end date (${formatted}). Please extend the recovery timeline or choose an earlier deadline.`,
        );
        return;
      }
    }

    const now = new Date().toISOString();
    if (draft.id) {
      setRows((prev) =>
        prev.map((g) =>
          g.id === draft.id
            ? {
                ...g,
                name: draft.name,
                recoveryNeed: draft.recoveryNeed,
                startDate: draft.startDate || null,
                deadlineDate: draft.deadlineDate || null,
                status: draft.status,
                complete: draft.status === 'Goal Met',
                updatedAt: now,
              }
            : g,
        ),
      );
    } else {
      setRows((prev) => [
        ...prev,
        {
          id: `goal-${Date.now()}`,
          caseId: caseRecord.caseId,
          clientId,
          name: draft.name,
          recoveryNeed: draft.recoveryNeed,
          startDate: draft.startDate || null,
          deadlineDate: draft.deadlineDate || null,
          status: draft.status,
          complete: draft.status === 'Goal Met',
          notes: [],
          createdAt: now,
          updatedAt: now,
        },
      ]);
    }
    setGoalOpen(false);
  };

  const confirmDelete = () => {
    if (deleteId) setRows((prev) => prev.filter((g) => g.id !== deleteId));
    setDeleteId(null);
  };

  const saveNote = () => {
    if (!noteFor || !noteText.trim()) return;
    const note: GoalNote = {
      id: `gn-${Date.now()}`,
      text: noteText.trim(),
      timestamp: new Date().toISOString(),
    };
    setRows((prev) =>
      prev.map((g) => (g.id === noteFor.id ? { ...g, notes: [...g.notes, note] } : g)),
    );
    setNoteText('');
    setNoteFor(null);
  };

  const isCustomTimeline = !!plan.recoveryTimeline?.startsWith('Custom (ends');

  return (
    <Stack spacing={2}>
      <Card variant="outlined">
        <CardContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                size="small"
                fullWidth
                label="Recovery Plan Date"
                value={plan.recoveryPlanDate
                  ? new Date(`${plan.recoveryPlanDate}T00:00:00`).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                  : 'Not set'}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <Tooltip title="Change plan date">
                      <IconButton size="small" onClick={() => { setPlanDateDraft(plan.recoveryPlanDate); setPlanDateConfirmOpen(true); }}>
                        <Box sx={{ '& svg': { width: 16, height: 16 } }}><LockOutline /></Box>
                      </IconButton>
                    </Tooltip>
                  ),
                }}
                sx={{ '& .MuiInputBase-input': { cursor: 'default', color: (t) => t.palette.text.primary } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                size="small"
                fullWidth
                label="Recovery Timeline"
                value={isCustomTimeline ? '__custom__' : (plan.recoveryTimeline ?? '')}
                onChange={(e) => {
                  if (e.target.value === '__custom__') {
                    setPlan((p) => ({ ...p, recoveryTimeline: 'Custom (ends )' }));
                  } else {
                    setPlan((p) => ({ ...p, recoveryTimeline: e.target.value }));
                  }
                }}
              >
                <MenuItem value="">—</MenuItem>
                {RECOVERY_TIMELINE_OPTIONS.map((o) => (
                  <MenuItem key={o} value={o}>{o}</MenuItem>
                ))}
                <MenuItem value="__custom__">Custom end date...</MenuItem>
              </TextField>
              {isCustomTimeline && (
                <TextField
                  size="small"
                  fullWidth
                  label="Plan end date"
                  type="date"
                  value={customEndMatch?.[1] ?? ''}
                  inputProps={{ min: plan.recoveryPlanDate || undefined }}
                  onChange={(e) => {
                    if (e.target.value) {
                      setPlan((p) => ({ ...p, recoveryTimeline: `Custom (ends ${e.target.value})` }));
                    }
                  }}
                  sx={{ mt: 1 }}
                />
              )}
            </Grid>
          </Grid>
          {planEndDate && plan.recoveryPlanDate && planTotalDays !== null && (
            <DaysRemainingBanner
              planStartDate={plan.recoveryPlanDate}
              planEndDate={planEndDate}
              totalDays={planTotalDays}
            />
          )}
          <SaveBar onSave={save} label="Save plan" />
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="subtitle2" sx={{ pb: 1, mb: 1, borderBottom: 1, borderColor: 'divider' }}>Goal timeline</Typography>
          {plan.recoveryPlanDate && planTotalDays !== null ? (
            rows.length > 0 ? (
              <RecoveryGanttChart rows={rows} planStartDate={plan.recoveryPlanDate} totalDays={planTotalDays} />
            ) : (
              <Typography variant="body2" sx={{ color: (t) => t.palette.text.secondary, py: 2, textAlign: 'center' }}>
                Add recovery goals to see the timeline.
              </Typography>
            )
          ) : (
            <Typography variant="body2" sx={{ color: (t) => t.palette.text.secondary, py: 2, textAlign: 'center' }}>
              Set a recovery plan date and timeline above to see the goal chart.
            </Typography>
          )}
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ pb: 1, mb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2">Recovery goals</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>{rows.length} goals · {rows.filter((g) => g.notes.length > 0).reduce((acc, g) => acc + g.notes.length, 0)} notes</Typography>
              <Button size="small" variant="contained" startIcon={<Plus />} onClick={openAddGoal}>Add recovery goal</Button>
            </Stack>
          </Stack>
          <TableContainer sx={{ maxHeight: 480 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell align="center">Goal</TableCell>
                  <TableCell align="center">Recovery Need</TableCell>
                  <TableCell align="center" sx={{ width: 110 }}>Start</TableCell>
                  <TableCell align="center" sx={{ width: 110 }}>Deadline</TableCell>
                  <TableCell align="center" sx={{ width: 120 }}>Status</TableCell>
                  <TableCell align="right" sx={{ width: 100 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((goal) => {
                  const expanded = expandedId === goal.id;
                  return (
                    <Fragment key={goal.id}>
                      <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => setExpandedId(expanded ? null : goal.id)}>
                        <TableCell sx={{ width: 32 }}>
                          <Box sx={{ display: 'flex', transform: expanded ? 'rotate(180deg)' : 'none', transition: (t) => t.transitions.create('transform'), '& svg': { width: 16, height: 16 } }}>
                            <ChevronDown />
                          </Box>
                        </TableCell>
                        <TableCell align="center"><Typography variant="body2" sx={{ fontWeight: 600 }}>{goal.name}</Typography></TableCell>
                        <TableCell align="center"><Typography variant="body2">{goal.recoveryNeed}</Typography></TableCell>
                        <TableCell align="center">{goal.startDate ?? '—'}</TableCell>
                        <TableCell align="center">{goal.deadlineDate ?? '—'}</TableCell>
                        <TableCell align="center"><CaseStatusBadge status={goal.status} /></TableCell>
                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <Tooltip title="Add note"><IconButton size="small" onClick={() => { setNoteFor(goal); setNoteText(''); }}><Plus /></IconButton></Tooltip>
                            <Tooltip title="Edit"><IconButton size="small" onClick={() => openEditGoal(goal)}><Pencil /></IconButton></Tooltip>
                            <Tooltip title="Delete"><IconButton size="small" onClick={() => setDeleteId(goal.id)}><TrashCan /></IconButton></Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                      {expanded && (
                        <TableRow>
                          <TableCell colSpan={7} sx={{ bgcolor: 'action.hover', py: 1.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: (t) => t.palette.text.secondary }}>Notes ({goal.notes.length})</Typography>
                            {goal.notes.length === 0 ? (
                              <Typography variant="body2" sx={{ color: (t) => t.palette.text.secondary, mt: 0.5 }}>No notes yet.</Typography>
                            ) : (
                              <Stack spacing={1} sx={{ mt: 0.5 }}>
                                {goal.notes.map((n) => (
                                  <Box key={n.id}>
                                    <Typography variant="body2">{n.text}</Typography>
                                    <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>
                                      {new Date(n.timestamp).toLocaleDateString()} {new Date(n.timestamp).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} &mdash; {caseRecord.assignedManager}
                                    </Typography>
                                  </Box>
                                ))}
                              </Stack>
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
                {rows.length === 0 && (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3 }}><Typography variant="body2" sx={{ color: (t) => t.palette.text.secondary }}>No recovery goals created.</Typography></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Plan date change confirmation */}
      {(() => {
        const conflictingGoals = planDateDraft
          ? rows.filter((g) => g.startDate && g.startDate < planDateDraft)
          : [];
        const hasConflict = conflictingGoals.length > 0;
        return (
          <Dialog open={planDateConfirmOpen} onClose={() => setPlanDateConfirmOpen(false)} maxWidth="xs" fullWidth>
            <DialogTitle>Change recovery plan date?</DialogTitle>
            <DialogContent dividers>
              <Alert severity="warning" sx={{ mb: 2 }}>
                The plan start date anchors the days-remaining countdown and the goal timeline chart. Changing it will shift all relative calculations.
              </Alert>
              <InputField
                label="New plan date"
                type="date"
                value={planDateDraft}
                onChange={(v) => setPlanDateDraft(v)}
              />
              {hasConflict && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  The following {conflictingGoals.length === 1 ? 'goal has a start date' : 'goals have start dates'} before{' '}
                  {new Date(`${planDateDraft}T00:00:00`).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}:
                  <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2 }}>
                    {conflictingGoals.map((g) => (
                      <li key={g.id}>
                        <Typography variant="caption">{g.name} (starts {g.startDate})</Typography>
                      </li>
                    ))}
                  </Box>
                  Update or remove these goals before moving the plan start date forward.
                </Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPlanDateConfirmOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                disabled={!planDateDraft || hasConflict}
                onClick={() => { setPlan((p) => ({ ...p, recoveryPlanDate: planDateDraft })); setPlanDateConfirmOpen(false); }}
              >
                Update date
              </Button>
            </DialogActions>
          </Dialog>
        );
      })()}

      {/* Goal add/edit dialog */}
      <Dialog open={goalOpen} onClose={() => { setGoalDateError(null); setGoalOpen(false); }} maxWidth="sm" fullWidth>
        <DialogTitle>{draft.id ? 'Edit goal' : 'Add goal'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {goalDateError && (
              <Alert severity="error" onClose={() => setGoalDateError(null)}>
                {goalDateError}
              </Alert>
            )}
            <InputField
              label="Goal Name"
              value={draft.name}
              onChange={(v) => setDraft((p) => ({ ...p, name: v }))}
            />
            <SelectField
              label="Recovery Need"
              value={draft.recoveryNeed}
              onChange={(v) => setDraft((p) => ({ ...p, recoveryNeed: v }))}
              options={RECOVERY_NEED_LABELS}
            />
            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <InputField
                  label="Start Date"
                  type="date"
                  value={draft.startDate}
                  onChange={(v) => setDraft((p) => ({ ...p, startDate: v }))}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <InputField
                  label="Deadline"
                  type="date"
                  value={draft.deadlineDate}
                  onChange={(v) => setDraft((p) => ({ ...p, deadlineDate: v }))}
                  min={draft.startDate || undefined}
                />
              </Grid>
            </Grid>
            <SelectField
              label="Status"
              value={draft.status}
              onChange={(v) => setDraft((p) => ({ ...p, status: v }))}
              options={GOAL_STATUSES}
              allowEmpty={false}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGoalOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveGoal} disabled={!draft.name.trim()}>
            {draft.id ? 'Save changes' : 'Add goal'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add-note dialog */}
      <Dialog open={noteFor !== null} onClose={() => setNoteFor(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Add note — {noteFor?.name}</DialogTitle>
        <DialogContent dividers>
          {noteFor && noteFor.notes.length > 0 && (
            <Box sx={{ mb: (t) => t.spacing(2) }}>
              <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>
                Previous notes
              </Typography>
              <Stack spacing={1} sx={{ mt: (t) => t.spacing(1) }}>
                {noteFor.notes.map((n) => (
                  <Typography key={n.id} variant="body2">
                    {n.text}
                  </Typography>
                ))}
              </Stack>
              <Divider sx={{ mt: (t) => t.spacing(2) }} />
            </Box>
          )}
          <TextField
            label="Note"
            fullWidth
            multiline
            rows={4}
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteFor(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveNote} disabled={!noteText.trim()}>
            Add note
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={deleteId !== null} onClose={() => setDeleteId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete goal?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">This will remove the goal and its notes.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Referrals tab — add + edit referrals from resource library
// ---------------------------------------------------------------------------

interface ReferralDraft {
  id: string | null;
  resourceId: string;
  serviceName: string;
  agencyName: string;
  status: string;
  units: string;
  amount: string;
  notes: string;
}

const EMPTY_REFERRAL: ReferralDraft = {
  id: null,
  resourceId: '',
  serviceName: '',
  agencyName: '',
  status: 'Requested',
  units: '',
  amount: '',
  notes: '',
};

function ReferralsTab({
  referrals,
  resources,
  caseId,
  clientId,
}: {
  referrals: TcrReferral[];
  resources: TcrResource[];
  caseId: string;
  clientId: string;
}) {
  const [rows, setRows] = useState<TcrReferral[]>(referrals);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<ReferralDraft>(EMPTY_REFERRAL);

  const openAdd = () => setPickerOpen(true);

  const handleResourcePick = (resource: TcrResource) => {
    setDraft({
      ...EMPTY_REFERRAL,
      resourceId: resource.id,
      serviceName: resource.service,
      agencyName: resource.organization,
    });
    setOpen(true);
  };
  const openEdit = (ref: TcrReferral) => {
    setDraft({
      id: ref.id,
      resourceId: '',
      serviceName: ref.serviceName,
      agencyName: ref.agencyName,
      status: ref.status,
      units: ref.units != null ? String(ref.units) : '',
      amount: ref.amount != null ? String(ref.amount) : '',
      notes: ref.notes ?? '',
    });
    setOpen(true);
  };

  const handleSave = () => {
    if (!draft.serviceName.trim()) return;
    const now = new Date().toISOString();
    const units = draft.units ? Number(draft.units) : null;
    const amount = draft.amount ? Number(draft.amount) : null;
    if (draft.id) {
      setRows((prev) =>
        prev.map((r) =>
          r.id === draft.id
            ? {
                ...r,
                serviceName: draft.serviceName,
                agencyName: draft.agencyName,
                status: draft.status,
                units,
                amount,
                notes: draft.notes || null,
                updatedAt: now,
              }
            : r,
        ),
      );
    } else {
      setRows((prev) => [
        ...prev,
        {
          id: `ref-${Date.now()}`,
          caseId,
          clientId,
          interactionId: '',
          serviceName: draft.serviceName,
          agencyName: draft.agencyName,
          status: draft.status,
          units,
          costPerUnit: null,
          amount,
          fundingAmount: null,
          notes: draft.notes || null,
          requestedDate: now.slice(0, 10),
          referredDate: null,
          acceptedDate: null,
          providedDate: null,
          declinedDate: null,
          unreachedDate: null,
          createdAt: now,
          updatedAt: now,
        },
      ]);
    }
    setOpen(false);
  };

  return (
    <>
    <Card variant="outlined">
      <CardContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ pb: 1, mb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2">Referral history</Typography>
          <Button size="small" variant="contained" startIcon={<Plus />} onClick={openAdd}>Add referral</Button>
        </Stack>

      <TableContainer sx={{ maxHeight: 480 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell align="center">Service</TableCell>
              <TableCell align="center">Agency</TableCell>
              <TableCell align="center" sx={{ width: 90 }}>Status</TableCell>
              <TableCell align="center" sx={{ width: 60 }}>Units</TableCell>
              <TableCell align="center" sx={{ width: 80 }}>Amount</TableCell>
              <TableCell align="center" sx={{ width: 100 }}>Requested</TableCell>
              <TableCell align="center" sx={{ width: 100 }}>Provided</TableCell>
              <TableCell align="right" sx={{ width: 60 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((ref) => (
              <TableRow key={ref.id}>
                <TableCell align="center">{ref.serviceName}</TableCell>
                <TableCell align="center">{ref.agencyName}</TableCell>
                <TableCell align="center">
                  <CaseStatusBadge status={ref.status} />
                </TableCell>
                <TableCell align="center">{ref.units ?? '—'}</TableCell>
                <TableCell align="center">{ref.amount != null ? `$${Number(ref.amount).toLocaleString('en-US', { minimumFractionDigits: 0 })}` : '—'}</TableCell>
                <TableCell align="center">{ref.requestedDate ? new Date(`${ref.requestedDate}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</TableCell>
                <TableCell align="center">{ref.providedDate ? new Date(`${ref.providedDate}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => openEdit(ref)}>
                      <Pencil />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography
                    variant="body2"
                    sx={{ color: (t) => t.palette.text.secondary, textAlign: 'center' }}
                  >
                    No referrals recorded.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{draft.id ? 'Edit referral' : 'Add referral'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <InputField
              label="Service"
              value={draft.serviceName}
              onChange={(v) => setDraft((p) => ({ ...p, serviceName: v }))}
            />
            <InputField
              label="Agency"
              value={draft.agencyName}
              onChange={(v) => setDraft((p) => ({ ...p, agencyName: v }))}
            />
            <SelectField
              label="Status"
              value={draft.status}
              onChange={(v) => setDraft((p) => ({ ...p, status: v }))}
              options={REFERRAL_STATUSES}
              allowEmpty={false}
            />
            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <InputField
                  label="Units"
                  type="number"
                  value={draft.units}
                  onChange={(v) => setDraft((p) => ({ ...p, units: v }))}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <InputField
                  label="Amount ($)"
                  type="number"
                  value={draft.amount}
                  onChange={(v) => setDraft((p) => ({ ...p, amount: v }))}
                />
              </Grid>
            </Grid>
            <Box sx={{ width: '100%' }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: (t) => t.palette.text.secondary, display: 'block', mb: 0.5 }}>Notes</Typography>
              <textarea
                value={draft.notes}
                onChange={(e) => setDraft((p) => ({ ...p, notes: e.target.value }))}
                rows={4}
                placeholder="Additional details about this referral..."
                style={{ width: '100%', padding: '8px 12px', fontSize: 14, fontFamily: 'inherit', borderRadius: 4, border: '1px solid #ccc', resize: 'vertical', boxSizing: 'border-box' }}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!draft.serviceName.trim()}>
            {draft.id ? 'Save changes' : 'Add referral'}
          </Button>
        </DialogActions>
      </Dialog>
      </CardContent>
    </Card>

      <ResourcePickerDrawer
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        resources={resources}
        onSelect={handleResourcePick}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Notes & Follow-Up tab — aggregated timeline + filters + scheduler
// ---------------------------------------------------------------------------

interface AggregatedNote {
  id: string;
  source: string;
  date: string;
  text: string;
  meta?: string;
}

interface ScheduledCheckIn {
  id: string;
  date: string;
  done: boolean;
}

function addDays(base: Date, days: number): string {
  const d = new Date(base);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function NotesFollowUpTab({
  caseRecord,
  interactions,
  followUp,
  goals,
  referrals,
}: {
  caseRecord: TcrCase;
  interactions: TcrInteraction[];
  followUp: FollowUp | null;
  goals: TcrGoal[];
  referrals: TcrReferral[];
}) {
  const save = useMockSave();
  const { currentUser } = useVcm();
  const [localInteractions, setLocalInteractions] = useState<TcrInteraction[]>(interactions);
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteType, setNoteType] = useState<string>(NOTE_TYPES[0]);
  const [noteText, setNoteText] = useState('');

  const addInteraction = () => {
    if (!noteText.trim()) return;
    const now = new Date();
    setLocalInteractions((prev) => [
      {
        id: `int-${now.getTime()}`,
        caseId: caseRecord.caseId,
        clientId: caseRecord.clientId,
        noteType,
        interactionDate: now.toISOString().slice(0, 10),
        narrative: noteText.trim(),
        createdBy: currentUser.name,
        createdAt: now.toISOString(),
      },
      ...prev,
    ]);
    setNoteText('');
    setNoteOpen(false);
  };

  const aggregated = useMemo<AggregatedNote[]>(() => {
    const out: AggregatedNote[] = [];
    for (const i of localInteractions) {
      out.push({
        id: i.id,
        source: 'Case Notes',
        date: i.interactionDate,
        text: i.narrative,
        meta: `${i.noteType} · ${i.createdBy}`,
      });
    }
    if (caseRecord.housingNotes) {
      out.push({
        id: `housing-${caseRecord.caseId}`,
        source: 'Housing',
        date: caseRecord.updatedAt.slice(0, 10),
        text: caseRecord.housingNotes,
      });
    }
    for (const g of goals) {
      for (const n of g.notes) {
        out.push({
          id: n.id,
          source: 'Recovery Plan',
          date: n.timestamp.slice(0, 10),
          text: n.text,
          meta: g.name,
        });
      }
    }
    for (const r of referrals) {
      if (r.notes) {
        out.push({
          id: `refn-${r.id}`,
          source: 'Referrals',
          date: r.updatedAt.slice(0, 10),
          text: r.notes,
          meta: r.serviceName,
        });
      }
    }
    for (const ins of caseRecord.insuranceEntries) {
      if (ins.notes) {
        out.push({
          id: `insn-${ins.id}`,
          source: 'Insurance',
          date: caseRecord.updatedAt.slice(0, 10),
          text: ins.notes,
          meta: ins.coverageType,
        });
      }
    }
    if (caseRecord.femaReasonForDenial) {
      out.push({
        id: `fema-${caseRecord.caseId}`,
        source: 'Assistance',
        date: caseRecord.femaInitialDeterminationDate ?? caseRecord.updatedAt.slice(0, 10),
        text: `VA Claim: ${caseRecord.femaReasonForDenial}`,
      });
    }
    if (caseRecord.qualitativeClosureSummary) {
      out.push({
        id: `closure-${caseRecord.caseId}`,
        source: 'Closure',
        date: caseRecord.dateClosed ?? caseRecord.updatedAt.slice(0, 10),
        text: caseRecord.qualitativeClosureSummary,
      });
    }
    return out.sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [caseRecord, localInteractions, goals, referrals]);

  const sources = useMemo(() => {
    const counts = new Map<string, number>();
    for (const n of aggregated) counts.set(n.source, (counts.get(n.source) ?? 0) + 1);
    return [...counts.entries()];
  }, [aggregated]);

  const [sourceFilter, setSourceFilter] = useState<string>('All');
  const visible = aggregated.filter((n) => sourceFilter === 'All' || n.source === sourceFilter);

  // Follow-up scheduler state
  const [fuType, setFuType] = useState(followUp?.followUpType ?? '');
  const [bestTime, setBestTime] = useState(followUp?.bestTimeToContact ?? '');
  const [nextDate, setNextDate] = useState(followUp?.nextFollowUpDate ?? '');
  const [fuNotes, setFuNotes] = useState(followUp?.notes ?? '');
  const [checkIns, setCheckIns] = useState<ScheduledCheckIn[]>([]);

  const enableRecurrence = (days: number) => {
    const base = new Date();
    setCheckIns(
      Array.from({ length: 4 }, (_, i) => ({
        id: `ci-${days}-${i}`,
        date: addDays(base, days * (i + 1)),
        done: false,
      })),
    );
  };
  const toggleCheckIn = (id: string) =>
    setCheckIns((prev) => prev.map((c) => (c.id === id ? { ...c, done: !c.done } : c)));

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, md: 7 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: (t) => t.spacing(1) }}
        >
          <Typography variant="subtitle2">Activity Timeline ({aggregated.length})</Typography>
          <Button size="small" variant="contained" startIcon={<Plus />} onClick={() => setNoteOpen(true)}>
            Add case note
          </Button>
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: (t) => t.spacing(2) }}>
          <Chip
            label={`All (${aggregated.length})`}
            color={sourceFilter === 'All' ? 'primary' : 'default'}
            variant={sourceFilter === 'All' ? 'filled' : 'outlined'}
            size="small"
            onClick={() => setSourceFilter('All')}
          />
          {sources.map(([source, count]) => (
            <Chip
              key={source}
              label={`${source} (${count})`}
              color={sourceFilter === source ? 'primary' : 'default'}
              variant={sourceFilter === source ? 'filled' : 'outlined'}
              size="small"
              onClick={() => setSourceFilter(source)}
            />
          ))}
        </Stack>
        <Stack spacing={2}>
          {visible.map((n) => (
            <Card key={n.id} variant="outlined">
              <CardContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: (t) => t.spacing(1) }}>
                  <Chip label={n.source} size="small" variant="outlined" />
                  <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>
                    {n.date}
                  </Typography>
                  {n.meta && (
                    <Typography
                      variant="caption"
                      sx={{ color: (t) => t.palette.text.secondary, ml: 'auto' }}
                    >
                      {n.meta}
                    </Typography>
                  )}
                </Stack>
                <Typography variant="body2">{n.text}</Typography>
              </CardContent>
            </Card>
          ))}
          {visible.length === 0 && (
            <Typography variant="body2" sx={{ color: (t) => t.palette.text.secondary }}>
              No notes for this filter.
            </Typography>
          )}
        </Stack>
      </Grid>

      <Grid size={{ xs: 12, md: 5 }}>
        <Card variant="outlined">
          <CardContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="subtitle2" sx={{ pb: 1, mb: 1.5, borderBottom: 1, borderColor: "divider" }}>
              Follow-Up
            </Typography>
            <Stack spacing={2}>
              <SelectField
                label="Follow-Up Type"
                value={fuType}
                onChange={setFuType}
                options={FOLLOW_UP_TYPES}
              />
              <InputField label="Best Time to Contact" value={bestTime} onChange={setBestTime} />
              <InputField
                label="Next Follow-Up Date"
                type="date"
                value={nextDate}
                onChange={setNextDate}
              />
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {[1, 3, 7, 30].map((d) => (
                  <Button
                    key={d}
                    size="small"
                    variant="outlined"
                    startIcon={<CalendarBlank />}
                    onClick={() => setNextDate(addDays(new Date(), d))}
                  >
                    {d} day
                  </Button>
                ))}
              </Stack>
              <Box sx={{ width: '100%' }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: (t) => t.palette.text.secondary, display: 'block', mb: 0.5 }}>Follow-Up Notes</Typography>
                <textarea value={fuNotes} onChange={(e) => setFuNotes(e.target.value)} rows={4} style={{ width: '100%', padding: '8px 12px', fontSize: 14, fontFamily: 'inherit', borderRadius: 4, border: '1px solid #ccc', resize: 'vertical', boxSizing: 'border-box' }} />
              </Box>
            </Stack>
            <SaveBar onSave={save} label="Save follow-up" />
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ mt: (t) => t.spacing(3) }}>
          <CardContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="subtitle2" sx={{ pb: 1, mb: 1.5, borderBottom: 1, borderColor: "divider" }}>
              Routine Check-ins
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: (t) => t.spacing(1) }}>
              {RECURRENCE_OPTIONS.map((opt) => (
                <Button
                  key={opt.label}
                  size="small"
                  variant="outlined"
                  onClick={() => enableRecurrence(opt.days)}
                >
                  {opt.label}
                </Button>
              ))}
              {checkIns.length > 0 && (
                <Button size="small" color="error" onClick={() => setCheckIns([])}>
                  Clear
                </Button>
              )}
            </Stack>
            <Stack spacing={1}>
              {checkIns.map((c) => (
                <FormControlLabel
                  key={c.id}
                  control={
                    <Checkbox
                      size="small"
                      checked={c.done}
                      onChange={() => toggleCheckIn(c.id)}
                    />
                  }
                  label={c.date}
                />
              ))}
              {checkIns.length === 0 && (
                <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>
                  Choose a cadence to generate scheduled check-ins.
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      <Dialog open={noteOpen} onClose={() => setNoteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add case note</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <SelectField
              label="Note Type"
              value={noteType}
              onChange={setNoteType}
              options={NOTE_TYPES}
              allowEmpty={false}
            />
            <TextField
              label="Narrative"
              fullWidth
              multiline
              rows={4}
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={addInteraction} disabled={!noteText.trim()}>
            Add note
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}

// ---------------------------------------------------------------------------
// Documents tab — upload / view / download / remove + group by type
// ---------------------------------------------------------------------------

interface UploadedDoc extends TcrDocument {
  blobUrl?: string;
}

function DocumentsTab({ documents }: { documents: TcrDocument[] }) {
  const [rows, setRows] = useState<UploadedDoc[]>(documents);
  const [docType, setDocType] = useState<string>(DOCUMENT_TYPES[0]);
  const [notes, setNotes] = useState('');
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const grouped = useMemo(() => {
    const map = new Map<string, UploadedDoc[]>();
    for (const d of rows) {
      const list = map.get(d.documentType) ?? [];
      list.push(d);
      map.set(d.documentType, list);
    }
    return [...map.entries()];
  }, [rows]);

  const addFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const now = new Date();
    const added: UploadedDoc[] = Array.from(files).map((file, i) => ({
      id: `doc-${Date.now()}-${i}`,
      caseId: '',
      clientId: '',
      fileName: file.name,
      documentType: docType,
      notes: notes || null,
      fileSize: `${Math.max(1, Math.round(file.size / 1024))} KB`,
      uploadDate: now.toLocaleDateString(),
      uploadedBy: 'You',
      blobUrl: URL.createObjectURL(file),
    }));
    setRows((prev) => [...added, ...prev]);
    setNotes('');
  };

  const remove = (id: string) =>
    setRows((prev) => {
      const target = prev.find((d) => d.id === id);
      if (target?.blobUrl) URL.revokeObjectURL(target.blobUrl);
      return prev.filter((d) => d.id !== id);
    });

  const view = (doc: UploadedDoc) => {
    if (doc.blobUrl) window.open(doc.blobUrl, '_blank', 'noopener');
  };

  return (
    <Stack spacing={2}>
      <Card variant="outlined">
        <CardContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="subtitle2" sx={{ pb: 1, mb: 1.5, borderBottom: 1, borderColor: "divider" }}>
            Upload Documents
          </Typography>
          <Grid container spacing={2} sx={{ mb: (t) => t.spacing(2) }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <SelectField
                label="Document Type"
                value={docType}
                onChange={setDocType}
                options={DOCUMENT_TYPES}
                allowEmpty={false}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <InputField label="Notes" value={notes} onChange={setNotes} />
            </Grid>
          </Grid>
          <Box
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              addFiles(e.dataTransfer.files);
            }}
            sx={{
              p: (t) => t.spacing(4),
              textAlign: 'center',
              border: (t) =>
                `1px dashed ${dragging ? t.palette.primary.main : t.palette.divider}`,
              borderRadius: (t) => t.shape.borderRadius,
              bgcolor: (t) => (dragging ? t.palette.action.hover : 'transparent'),
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                color: (t) => t.palette.text.secondary,
                mb: (t) => t.spacing(1),
                '& svg': { width: 28, height: 28 },
              }}
            >
              <TrayArrowUp />
            </Box>
            <Typography variant="body2" sx={{ mb: (t) => t.spacing(1) }}>
              Drag &amp; drop files here, or
            </Typography>
            <Button variant="outlined" onClick={() => fileInputRef.current?.click()}>
              Select files
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              onChange={(e) => addFiles(e.target.files)}
            />
          </Box>
        </CardContent>
      </Card>

      {grouped.map(([type, docs]) => (
        <Box key={type}>
          <Typography variant="subtitle2" sx={{ pb: 1, mb: 1.5, borderBottom: 1, borderColor: "divider" }}>
            {type} ({docs.length})
          </Typography>
          <TableContainer sx={{ maxHeight: 480 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell align="center">File Name</TableCell>
                  <TableCell align="center">Size</TableCell>
                  <TableCell align="center">Upload Date</TableCell>
                  <TableCell align="center">Uploaded By</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {docs.map((doc) => (
                  <TableRow key={doc.id}>
                    <TableCell align="center">{doc.fileName}</TableCell>
                    <TableCell align="center">{doc.fileSize}</TableCell>
                    <TableCell align="center">{doc.uploadDate}</TableCell>
                    <TableCell align="center">{doc.uploadedBy}</TableCell>
                    <TableCell align="right">
                      <Tooltip title={doc.blobUrl ? 'View' : 'Preview unavailable for seed data'}>
                        <span>
                          <IconButton size="small" onClick={() => view(doc)} disabled={!doc.blobUrl}>
                            <Eye />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Remove">
                        <IconButton size="small" onClick={() => remove(doc.id)}>
                          <TrashCan />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}
      {rows.length === 0 && (
        <Typography variant="body2" sx={{ color: (t) => t.palette.text.secondary }}>
          No documents uploaded.
        </Typography>
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Intake Assessment tab — embedded Survey123 form with incident selector + pre-fill
// ---------------------------------------------------------------------------

import { VSO_INCIDENTS } from '../lib/vcm/istat';

// ---------------------------------------------------------------------------
// Assessment photos — the two things the VSO call hard-requires:
//   1. open the per-assessment OneDrive folder via its link (from the assessment record).
//   2. add photos to that folder (upload-only — no delete).
// MOCK: local only, not wired to OneDrive/gab-core. No in-app photo preview/
// gallery (VSO did not ask for it — photos are viewed in OneDrive itself).
// ---------------------------------------------------------------------------

function IstatPhotosPanel({ caseRecord: _caseRecord }: { caseRecord: TcrCase }) {
  const [added, setAdded] = useState<Array<{ id: string; fileName: string }>>([]);
  const [warning, setWarning] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const remaining = Math.max(0, ISTAT_PHOTO_MAX - added.length);
  const atLimit = remaining === 0;

  const addFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const images = Array.from(files).filter((f) => f.type.startsWith('image/'));
    if (images.length === 0) {
      setWarning('Only image files can be added.');
      return;
    }
    const accepted = images.slice(0, remaining);
    if (accepted.length < images.length) {
      setWarning(`Photo limit reached — added ${accepted.length} of ${images.length} (max ${ISTAT_PHOTO_MAX} per assessment).`);
    } else {
      setWarning(null);
    }
    const now = Date.now();
    setAdded((prev) => [
      ...prev,
      ...accepted.map((file, i) => ({ id: `istat-upload-${now}-${i}`, fileName: file.name })),
    ]);
  };

  return (
    <Card variant="outlined">
      <CardContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="subtitle2" sx={{ pb: 1, mb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          Assessment Photos
        </Typography>

        <Alert severity="info" sx={{ mb: (t) => t.spacing(2) }}>
          Attach photos to this assessment record. (Demo — photos selected here are not yet uploaded.)
        </Alert>

        {warning && (
          <Alert severity="warning" sx={{ mb: (t) => t.spacing(2) }} onClose={() => setWarning(null)}>
            {warning}
          </Alert>
        )}

        <Box
          onDragOver={(e) => {
            if (atLimit) return;
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            if (!atLimit) addFiles(e.dataTransfer.files);
          }}
          sx={{
            p: (t) => t.spacing(4),
            textAlign: 'center',
            border: (t) => `1px dashed ${dragging ? t.palette.primary.main : t.palette.divider}`,
            borderRadius: (t) => t.shape.borderRadius,
            bgcolor: (t) => (dragging ? t.palette.action.hover : 'transparent'),
            opacity: atLimit ? 0.6 : 1,
          }}
        >
          <Box
            aria-hidden="true"
            sx={{
              display: 'flex',
              justifyContent: 'center',
              color: (t) => t.palette.text.secondary,
              mb: (t) => t.spacing(1),
              '& svg': { width: 28, height: 28 },
            }}
          >
            <TrayArrowUp />
          </Box>
          <Typography variant="body2" sx={{ mb: (t) => t.spacing(1) }}>
            {atLimit
              ? `Photo limit reached (${ISTAT_PHOTO_MAX} of ${ISTAT_PHOTO_MAX}).`
              : 'Drag & drop photos here'}
          </Typography>
          <Button variant="outlined" disabled={atLimit} onClick={() => fileInputRef.current?.click()}>
            Select photos
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            hidden
            aria-label="Add assessment photos"
            onChange={(e) => addFiles(e.target.files)}
          />
        </Box>

        {added.length > 0 && (
          <Stack spacing={0.5} sx={{ mt: (t) => t.spacing(2) }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: (t) => t.palette.text.secondary }}>
              Added this session ({added.length} of {ISTAT_PHOTO_MAX}):
            </Typography>
            {added.map((f) => (
              <Typography key={f.id} variant="body2">
                {f.fileName}
              </Typography>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// AssessmentForm — in-app form that replaces the Survey123 iframe.
// Dispatches to one of four sub-forms based on the program ID.
// ---------------------------------------------------------------------------

type FormType = 'ssvf' | 'housing' | 'general' | 'crisis';

function resolveFormType(programId: string): FormType {
  if (programId === 'SSVF-TX-2026-001' || programId === 'SSVF-TX-2025-001') return 'ssvf';
  if (programId === 'VHAP-TX-2026-001' || programId === 'VHAP-TX-2025-001') return 'housing';
  if (programId === 'TVC-GENERAL-2026') return 'general';
  if (programId === 'TVC-CRISIS-2026') return 'crisis';
  return 'general';
}

function SectionHeader({ label }: { label: string }) {
  return (
    <>
      <Typography variant="subtitle2" sx={{ mt: (t) => t.spacing(3), mb: (t) => t.spacing(1) }}>
        {label}
      </Typography>
      <Divider sx={{ mb: (t) => t.spacing(2) }} />
    </>
  );
}

function YesNoRadio({ id, label, value, onChange }: { id: string; label: string; value: string; onChange: (v: string) => void }) {
  return (
    <Box>
      <FormLabel id={`${id}-label`} sx={{ display: 'block', mb: (t) => t.spacing(0.5), fontSize: '0.875rem' }}>
        {label}
      </FormLabel>
      <RadioGroup
        row
        aria-labelledby={`${id}-label`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes" />
        <FormControlLabel value="no" control={<Radio size="small" />} label="No" />
      </RadioGroup>
    </Box>
  );
}

// --- SSVF Needs Assessment ---

function SsvfForm({ caseRecord, client: _client, onSubmit }: { caseRecord: TcrCase; client: TcrClient | null; onSubmit: () => void }) {
  const [monthlyIncome, setMonthlyIncome] = useState('');
  const [dependents, setDependents] = useState('');
  const [housingSituation, setHousingSituation] = useState('');
  const [monthlyRent, setMonthlyRent] = useState('');
  const [monthsBehind, setMonthsBehind] = useState('');
  const [vaHealthcare, setVaHealthcare] = useState('');
  const [serviceConnected, setServiceConnected] = useState('');
  const [disabilityRating, setDisabilityRating] = useState('');
  const [primaryAssistance, setPrimaryAssistance] = useState('');
  const [caseNotes, setCaseNotes] = useState(caseRecord.damageNotes ?? '');

  return (
    <Stack spacing={2}>
      <SectionHeader label="Household" />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="Monthly household income"
            fullWidth
            size="small"
            value={monthlyIncome}
            onChange={(e) => setMonthlyIncome(e.target.value)}
            slotProps={{ input: { startAdornment: <Typography variant="body2" sx={{ mr: (t) => t.spacing(0.5) }}>$</Typography> } }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="Number of dependents in household"
            type="number"
            fullWidth
            size="small"
            value={dependents}
            onChange={(e) => setDependents(e.target.value)}
            slotProps={{ htmlInput: { min: 0 } }}
          />
        </Grid>
      </Grid>

      <SectionHeader label="Housing" />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            select
            label="Current housing situation"
            fullWidth
            size="small"
            value={housingSituation}
            onChange={(e) => setHousingSituation(e.target.value)}
          >
            <MenuItem value="">Select...</MenuItem>
            {['Owned', 'Renting', 'Staying with family/friends', 'Emergency shelter', 'Unsheltered'].map((o) => (
              <MenuItem key={o} value={o}>{o}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="Monthly rent/mortgage payment"
            fullWidth
            size="small"
            value={monthlyRent}
            onChange={(e) => setMonthlyRent(e.target.value)}
            slotProps={{ input: { startAdornment: <Typography variant="body2" sx={{ mr: (t) => t.spacing(0.5) }}>$</Typography> } }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="Months behind on rent/mortgage"
            type="number"
            fullWidth
            size="small"
            value={monthsBehind}
            onChange={(e) => setMonthsBehind(e.target.value)}
            slotProps={{ htmlInput: { min: 0, max: 24 } }}
          />
        </Grid>
      </Grid>

      <SectionHeader label="VA Benefits" />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <YesNoRadio id="ssvf-va-healthcare" label="Enrolled in VA healthcare" value={vaHealthcare} onChange={setVaHealthcare} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <YesNoRadio id="ssvf-service-connected" label="Service-connected disability" value={serviceConnected} onChange={setServiceConnected} />
        </Grid>
        {serviceConnected === 'yes' && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Disability rating (%)"
              type="number"
              fullWidth
              size="small"
              value={disabilityRating}
              onChange={(e) => setDisabilityRating(e.target.value)}
              slotProps={{ htmlInput: { min: 0, max: 100 } }}
            />
          </Grid>
        )}
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            select
            label="Primary assistance needed"
            fullWidth
            size="small"
            value={primaryAssistance}
            onChange={(e) => setPrimaryAssistance(e.target.value)}
          >
            <MenuItem value="">Select...</MenuItem>
            {['Rental assistance', 'Security deposit', 'Utility assistance', 'Move-in costs', 'Transportation', 'Other'].map((o) => (
              <MenuItem key={o} value={o}>{o}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField
            label="Case notes"
            fullWidth
            size="small"
            multiline
            minRows={3}
            value={caseNotes}
            onChange={(e) => setCaseNotes(e.target.value)}
          />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: (t) => t.spacing(1) }}>
        <Button variant="contained" onClick={onSubmit}>Submit Assessment</Button>
      </Box>
    </Stack>
  );
}

// --- Housing Assistance Assessment ---

function HousingForm({ caseRecord: _caseRecord, client, onSubmit }: { caseRecord: TcrCase; client: TcrClient | null; onSubmit: () => void }) {
  const [housingGoal, setHousingGoal] = useState('');
  const [livingSituation, setLivingSituation] = useState('');
  const [monthlyIncome, setMonthlyIncome] = useState('');
  const [vaLoanCoe, setVaLoanCoe] = useState('');
  const [dd214, setDd214] = useState('');
  const [hudVash, setHudVash] = useState('');
  const [accessibility, setAccessibility] = useState('');
  const [targetCounty, setTargetCounty] = useState(client?.primaryAddress?.county ?? '');
  const [specialConsiderations, setSpecialConsiderations] = useState('');

  return (
    <Stack spacing={2}>
      <SectionHeader label="Housing Goal" />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            select
            label="Housing goal"
            fullWidth
            size="small"
            value={housingGoal}
            onChange={(e) => setHousingGoal(e.target.value)}
          >
            <MenuItem value="">Select...</MenuItem>
            {['Rent an apartment', 'Purchase a home', 'Transitional housing', 'Other'].map((o) => (
              <MenuItem key={o} value={o}>{o}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            select
            label="Current living situation"
            fullWidth
            size="small"
            value={livingSituation}
            onChange={(e) => setLivingSituation(e.target.value)}
          >
            <MenuItem value="">Select...</MenuItem>
            {['Staying with family/friends', 'Emergency shelter', 'Hotel/motel', 'Unsheltered', 'Transitional housing'].map((o) => (
              <MenuItem key={o} value={o}>{o}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="Monthly income"
            fullWidth
            size="small"
            value={monthlyIncome}
            onChange={(e) => setMonthlyIncome(e.target.value)}
            slotProps={{ input: { startAdornment: <Typography variant="body2" sx={{ mr: (t) => t.spacing(0.5) }}>$</Typography> } }}
          />
        </Grid>
      </Grid>

      <SectionHeader label="Eligibility" />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <YesNoRadio id="housing-va-loan-coe" label="VA home loan certificate of eligibility" value={vaLoanCoe} onChange={setVaLoanCoe} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <YesNoRadio id="housing-dd214" label="DD-214 on file" value={dd214} onChange={setDd214} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <YesNoRadio id="housing-hud-vash" label="HUD-VASH voucher holder" value={hudVash} onChange={setHudVash} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <YesNoRadio id="housing-accessibility" label="Accessibility requirements" value={accessibility} onChange={setAccessibility} />
        </Grid>
      </Grid>

      <SectionHeader label="Needs" />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="Target county"
            fullWidth
            size="small"
            value={targetCounty}
            onChange={(e) => setTargetCounty(e.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField
            label="Special considerations"
            fullWidth
            size="small"
            multiline
            minRows={3}
            value={specialConsiderations}
            onChange={(e) => setSpecialConsiderations(e.target.value)}
          />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: (t) => t.spacing(1) }}>
        <Button variant="contained" onClick={onSubmit}>Submit Assessment</Button>
      </Box>
    </Stack>
  );
}

// --- General Benefits Assessment ---

const GENERAL_BENEFITS_OPTIONS = ['VA Disability', 'VA Healthcare', 'TRICARE', 'Medicare', 'Social Security', 'Food Stamps/SNAP', 'None'] as const;

function GeneralForm({ caseRecord: _caseRecord, client, onSubmit }: { caseRecord: TcrCase; client: TcrClient | null; onSubmit: () => void }) {
  const [claimFiled, setClaimFiled] = useState('');
  const [disabilityRating, setDisabilityRating] = useState('');
  const [vaHealthcareEnrolled, setVaHealthcareEnrolled] = useState('');
  const [nearestVaFacility, setNearestVaFacility] = useState(client?.primaryAddress?.city ?? '');
  const [giBillUsed, setGiBillUsed] = useState('');
  const [vocRehab, setVocRehab] = useState('');
  const [benefitsReceiving, setBenefitsReceiving] = useState<string[]>([]);
  const [additionalNotes, setAdditionalNotes] = useState('');

  const toggleBenefit = (benefit: string) => {
    setBenefitsReceiving((prev) =>
      prev.includes(benefit) ? prev.filter((b) => b !== benefit) : [...prev, benefit],
    );
  };

  return (
    <Stack spacing={2}>
      <SectionHeader label="VA Benefits" />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <YesNoRadio id="general-claim-filed" label="VA disability claim filed" value={claimFiled} onChange={setClaimFiled} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="Current disability rating (%)"
            type="number"
            fullWidth
            size="small"
            value={disabilityRating}
            onChange={(e) => setDisabilityRating(e.target.value)}
            slotProps={{ htmlInput: { min: 0, max: 100 } }}
          />
        </Grid>
      </Grid>

      <SectionHeader label="Healthcare" />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <YesNoRadio id="general-va-healthcare" label="VA healthcare enrolled" value={vaHealthcareEnrolled} onChange={setVaHealthcareEnrolled} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="Nearest VA facility"
            fullWidth
            size="small"
            value={nearestVaFacility}
            onChange={(e) => setNearestVaFacility(e.target.value)}
          />
        </Grid>
      </Grid>

      <SectionHeader label="Education" />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <YesNoRadio id="general-gi-bill" label="GI Bill benefits used" value={giBillUsed} onChange={setGiBillUsed} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <YesNoRadio id="general-voc-rehab" label="Vocational rehabilitation interest" value={vocRehab} onChange={setVocRehab} />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <FormLabel id="general-benefits-receiving-label" sx={{ display: 'block', mb: (t) => t.spacing(0.5), fontSize: '0.875rem' }}>
            Benefits currently receiving
          </FormLabel>
          <FormGroup aria-labelledby="general-benefits-receiving-label" row>
            {GENERAL_BENEFITS_OPTIONS.map((benefit) => (
              <FormControlLabel
                key={benefit}
                control={
                  <Checkbox
                    size="small"
                    checked={benefitsReceiving.includes(benefit)}
                    onChange={() => toggleBenefit(benefit)}
                  />
                }
                label={benefit}
              />
            ))}
          </FormGroup>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField
            label="Additional notes"
            fullWidth
            size="small"
            multiline
            minRows={3}
            value={additionalNotes}
            onChange={(e) => setAdditionalNotes(e.target.value)}
          />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: (t) => t.spacing(1) }}>
        <Button variant="contained" onClick={onSubmit}>Submit Assessment</Button>
      </Box>
    </Stack>
  );
}

// --- Crisis & Emergency Assessment ---

const CRISIS_URGENT_NEEDS = ['Food', 'Clothing', 'Transportation', 'Emergency shelter', 'Medical care', 'Mental health', 'Legal aid', 'Financial assistance'] as const;

function CrisisForm({ caseRecord: _caseRecord, client: _client, onSubmit }: { caseRecord: TcrCase; client: TcrClient | null; onSubmit: () => void }) {
  const [crisisNature, setCrisisNature] = useState('');
  const [immediateSafety, setImmediateSafety] = useState('');
  const [shelterStatus, setShelterStatus] = useState('');
  const [deadline, setDeadline] = useState('');
  const [urgentNeeds, setUrgentNeeds] = useState<string[]>([]);
  const [referredToPartner, setReferredToPartner] = useState('');
  const [partnerAgency, setPartnerAgency] = useState('');
  const [crisisNotes, setCrisisNotes] = useState('');

  const toggleNeed = (need: string) => {
    setUrgentNeeds((prev) =>
      prev.includes(need) ? prev.filter((n) => n !== need) : [...prev, need],
    );
  };

  return (
    <Stack spacing={2}>
      <SectionHeader label="Crisis Type" />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            select
            label="Nature of crisis"
            fullWidth
            size="small"
            value={crisisNature}
            onChange={(e) => setCrisisNature(e.target.value)}
          >
            <MenuItem value="">Select...</MenuItem>
            {[
              'Eviction/housing loss',
              'Utility shutoff',
              'Food insecurity',
              'Domestic situation',
              'Mental health crisis',
              'Substance use',
              'Medical emergency',
              'Legal issue',
              'Other',
            ].map((o) => (
              <MenuItem key={o} value={o}>{o}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <YesNoRadio id="crisis-immediate-safety" label="Immediate safety concern" value={immediateSafety} onChange={setImmediateSafety} />
        </Grid>
      </Grid>

      <SectionHeader label="Immediate Needs" />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            select
            label="Current shelter status"
            fullWidth
            size="small"
            value={shelterStatus}
            onChange={(e) => setShelterStatus(e.target.value)}
          >
            <MenuItem value="">Select...</MenuItem>
            {['Housed', 'At risk of losing housing', 'Emergency shelter', 'Unsheltered'].map((o) => (
              <MenuItem key={o} value={o}>{o}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="Time-sensitive deadline"
            type="date"
            fullWidth
            size="small"
            value={deadline}
            onChange={(e) => setDeadline(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <FormLabel id="crisis-urgent-needs-label" sx={{ display: 'block', mb: (t) => t.spacing(0.5), fontSize: '0.875rem' }}>
            Urgent needs
          </FormLabel>
          <FormGroup aria-labelledby="crisis-urgent-needs-label" row>
            {CRISIS_URGENT_NEEDS.map((need) => (
              <FormControlLabel
                key={need}
                control={
                  <Checkbox
                    size="small"
                    checked={urgentNeeds.includes(need)}
                    onChange={() => toggleNeed(need)}
                  />
                }
                label={need}
              />
            ))}
          </FormGroup>
        </Grid>
      </Grid>

      <SectionHeader label="Referrals" />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <YesNoRadio id="crisis-referred" label="Referred to partner agency" value={referredToPartner} onChange={setReferredToPartner} />
        </Grid>
        {referredToPartner === 'yes' && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Partner agency name"
              fullWidth
              size="small"
              value={partnerAgency}
              onChange={(e) => setPartnerAgency(e.target.value)}
            />
          </Grid>
        )}
        <Grid size={{ xs: 12 }}>
          <TextField
            label="Crisis notes"
            fullWidth
            size="small"
            multiline
            minRows={3}
            value={crisisNotes}
            onChange={(e) => setCrisisNotes(e.target.value)}
          />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: (t) => t.spacing(1) }}>
        <Button variant="contained" onClick={onSubmit}>Submit Assessment</Button>
      </Box>
    </Stack>
  );
}

// --- Dispatcher ---

const FORM_TITLES: Record<FormType, string> = {
  ssvf: 'SSVF Needs Assessment',
  housing: 'Housing Assistance Assessment',
  general: 'General Benefits Assessment',
  crisis: 'Crisis & Emergency Assessment',
};

interface AssessmentFormProps {
  programId: string;
  caseRecord: TcrCase;
  client: TcrClient | null;
  onSubmitted: () => void;
}

function AssessmentForm({ programId, caseRecord, client, onSubmitted }: AssessmentFormProps) {
  const save = useMockSave();
  const formType = resolveFormType(programId);

  const handleSubmit = () => {
    save();
    onSubmitted();
  };

  const formProps = { caseRecord, client, onSubmit: handleSubmit };

  return (
    <Card variant="outlined">
      <CardContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="subtitle2" sx={{ pb: 1, mb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          {FORM_TITLES[formType]}
        </Typography>
        {formType === 'ssvf' && <SsvfForm {...formProps} />}
        {formType === 'housing' && <HousingForm {...formProps} />}
        {formType === 'general' && <GeneralForm {...formProps} />}
        {formType === 'crisis' && <CrisisForm {...formProps} />}
      </CardContent>
    </Card>
  );
}

function IstatTab({ caseRecord, client }: { caseRecord: TcrCase; client: TcrClient | null }) {
  const [selectedIncident, setSelectedIncident] = useState('');
  const [formLoaded, setFormLoaded] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const addr = client?.primaryAddress;

  return (
    <Stack spacing={2}>
      {/* Event selector + pre-fill summary */}
      <Card variant="outlined">
        <CardContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="subtitle2" sx={{ pb: 1, mb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            Intake Assessment
          </Typography>
          <Typography variant="body2" sx={{ color: (t) => t.palette.text.secondary, mb: 2 }}>
            Select the program and load the assessment form. Client and case data will be pre-filled automatically.
          </Typography>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                label="Program"
                fullWidth
                size="small"
                value={selectedIncident}
                onChange={(e) => { setSelectedIncident(e.target.value); setFormLoaded(false); setSubmitted(false); }}
              >
                <MenuItem value="">Select a program...</MenuItem>
                {VSO_INCIDENTS.map((inc) => (
                  <MenuItem key={inc.id} value={inc.id}>{inc.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <Button
                variant="contained"
                fullWidth
                disabled={!selectedIncident}
                onClick={() => setFormLoaded(true)}
              >
                Start Assessment
              </Button>
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <FormControlLabel
                control={<Checkbox checked={submitted} onChange={(e) => setSubmitted(e.target.checked)} />}
                label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Assessment Submitted</Typography>}
              />
            </Grid>
          </Grid>

          {/* Pre-fill preview */}
          {selectedIncident && !formLoaded && (
            <Box sx={{ mt: 2, p: 1.5, borderRadius: 1, bgcolor: 'action.hover' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: (t) => t.palette.text.secondary, display: 'block', mb: 1 }}>
                Data that will be pre-filled:
              </Typography>
              <Grid container spacing={1}>
                <Grid size={{ xs: 4 }}><Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>Name: <b>{client?.firstName} {client?.lastName}</b></Typography></Grid>
                <Grid size={{ xs: 4 }}><Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>Phone: <b>{client?.phoneHome ?? '—'}</b></Typography></Grid>
                <Grid size={{ xs: 4 }}><Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>Email: <b>{client?.email ?? '—'}</b></Typography></Grid>
                <Grid size={{ xs: 4 }}><Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>Address: <b>{addr?.address1 ?? '—'}</b></Typography></Grid>
                <Grid size={{ xs: 4 }}><Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>County: <b>{addr?.county ?? caseRecord.county}</b></Typography></Grid>
                <Grid size={{ xs: 4 }}><Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>Damage: <b>{caseRecord.housingCondition ?? '—'}</b></Typography></Grid>
              </Grid>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Success alert shown after in-app form submission */}
      {submitted && (
        <Alert severity="success">
          Assessment submitted successfully. The record has been saved.
        </Alert>
      )}

      {/* In-app assessment form */}
      {formLoaded && !submitted && (
        <AssessmentForm
          programId={selectedIncident}
          caseRecord={caseRecord}
          client={client}
          onSubmitted={() => setSubmitted(true)}
        />
      )}

      <IstatPhotosPanel caseRecord={caseRecord} />
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Closure tab — editable closure form + checklist + confirm dialog
// ---------------------------------------------------------------------------

interface ChecklistItemState {
  checked: boolean;
  na: boolean;
  notes: string;
}

const EMPTY_CHECKLIST_ITEM: ChecklistItemState = { checked: false, na: false, notes: '' };

function ClosureTab({ caseRecord }: { caseRecord: TcrCase }) {
  const save = useMockSave();
  const [closureForm, setClosureForm] = useState({
    dateClosed: caseRecord.dateClosed ?? '',
    closureReason: caseRecord.closureReason ?? '',
    completedSatisfactionSurvey: caseRecord.completedSatisfactionSurvey ?? '',
    clientSecuredHousing: caseRecord.clientSecuredHousing ?? '',
    householdMemberWorkingPlan: caseRecord.householdMemberWorkingPlan ?? '',
    dateOfProgramClosure: caseRecord.dateOfProgramClosure ?? '',
    qualitativeClosureSummary: caseRecord.qualitativeClosureSummary ?? '',
  });
  const setCF = (k: keyof typeof closureForm, v: string) =>
    setClosureForm((p) => ({ ...p, [k]: v }));

  const [items, setItems] = useState<Record<string, ChecklistItemState>>(() => {
    const initial: Record<string, ChecklistItemState> = {};
    for (const item of CLOSURE_CHECKLIST_ITEMS) {
      initial[item] = {
        checked: caseRecord.closureChecklist[item] ?? false,
        na: false,
        notes: '',
      };
    }
    return initial;
  });
  const [closed, setClosed] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const setItem = (item: string, patch: Partial<ChecklistItemState>) =>
    setItems((prev) => ({ ...prev, [item]: { ...(prev[item] ?? EMPTY_CHECKLIST_ITEM), ...patch } }));
  const toggleChecked = (item: string, checked: boolean) =>
    setItem(item, { checked, na: checked ? false : (items[item]?.na ?? false) });
  const toggleNa = (item: string, na: boolean) =>
    setItem(item, { na, checked: na ? false : (items[item]?.checked ?? false) });

  const canClose = CLOSURE_CHECKLIST_ITEMS.every((item) => {
    const s = items[item] ?? EMPTY_CHECKLIST_ITEM;
    return s.checked || (s.na && s.notes.trim().length > 0);
  });

  return (
    <Stack spacing={2}>
      {/* Case closure fields — 3-column grid */}
      <Card variant="outlined">
        <CardContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="subtitle2" sx={{ pb: 1, mb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            Case closure
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <InputField label="Date Case Closed" type="date" value={closureForm.dateClosed} onChange={(v) => setCF('dateClosed', v)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <SelectField label="Reason for Case Closure" value={closureForm.closureReason} onChange={(v) => setCF('closureReason', v)} options={CLOSURE_REASONS} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <SelectField label="Completed Satisfaction Survey" value={closureForm.completedSatisfactionSurvey} onChange={(v) => setCF('completedSatisfactionSurvey', v)} options={['Yes', 'No'] as const} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <SelectField label="Client Secured Housing" value={closureForm.clientSecuredHousing} onChange={(v) => setCF('clientSecuredHousing', v)} options={['Yes', 'No'] as const} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <SelectField label="Household Member Working a Plan" value={closureForm.householdMemberWorkingPlan} onChange={(v) => setCF('householdMemberWorkingPlan', v)} options={['Yes', 'No'] as const} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <InputField label="Date of DCM Program Closure" type="date" value={closureForm.dateOfProgramClosure} onChange={(v) => setCF('dateOfProgramClosure', v)} />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Qualitative closure summary — full width */}
      <Card variant="outlined">
        <CardContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="subtitle2" sx={{ pb: 1, mb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            Qualitative closure summary
          </Typography>
          <Box sx={{ width: '100%' }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: (t) => t.palette.text.secondary, display: 'block', mb: 0.5 }}>
              Summary
            </Typography>
            <textarea
              value={closureForm.qualitativeClosureSummary}
              onChange={(e) => setCF('qualitativeClosureSummary', e.target.value)}
              rows={8}
              placeholder="Summarize the client recovery outcome, remaining considerations, and closure rationale."
              style={{ width: '100%', padding: '8px 12px', fontSize: 14, fontFamily: 'inherit', borderRadius: 4, border: '1px solid #ccc', resize: 'vertical', boxSizing: 'border-box' }}
            />
          </Box>
          <SaveBar onSave={save} label="Save closure info" />
        </CardContent>
      </Card>

      {/* Closure checklist — full width */}
      <Card variant="outlined">
        <CardContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="subtitle2" sx={{ pb: 1, mb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            Closure Checklist
          </Typography>
            <Stack spacing={2}>
              {CLOSURE_CHECKLIST_ITEMS.map((item) => {
                const s = items[item] ?? EMPTY_CHECKLIST_ITEM;
                return (
                  <Box key={item}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {item}
                    </Typography>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={s.checked}
                            onChange={(e) => toggleChecked(item, e.target.checked)}
                          />
                        }
                        label="Complete"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={s.na}
                            onChange={(e) => toggleNa(item, e.target.checked)}
                          />
                        }
                        label="N/A"
                      />
                    </Stack>
                    {s.na && (
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Reason required for N/A"
                        value={s.notes}
                        onChange={(e) => setItem(item, { notes: e.target.value })}
                        error={s.notes.trim().length === 0}
                      />
                    )}
                  </Box>
                );
              })}
            </Stack>

            <Divider sx={{ my: (t) => t.spacing(2) }} />

            {closed ? (
              <Alert severity="success">Case marked closed.</Alert>
            ) : (
              <Stack spacing={1}>
                {!canClose && (
                  <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>
                    Every item must be marked Complete, or N/A with a reason, before the case can be
                    closed.
                  </Typography>
                )}
                <Box>
                  <Button
                    variant="contained"
                    color="error"
                    disabled={!canClose}
                    onClick={() => setConfirmOpen(true)}
                  >
                    Close Case
                  </Button>
                </Box>
              </Stack>
          )}
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Close this case?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            Closing the case will make it read-only. You can reopen it from the Admin console if you
            have permission.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              setClosed(true);
              setConfirmOpen(false);
            }}
          >
            Close case
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

function isCaseTabId(value: string | null): value is CaseTabId {
  return value != null && CASE_TABS.some((t) => t.id === value);
}

export function CaseRecordPage() {
  const { caseId } = useParams<{ caseId: string }>();
  const { currentUser } = useVcm();
  const { buildPath, navigateToPath } = useAppNavigation();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: CaseTabId = isCaseTabId(tabParam) ? tabParam : 'summary';

  const handleTabChange = (next: CaseTabId) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  /* MOCK DATA — replace with real /v2 API calls when backend is connected */
  const caseRecord = mockGetCaseById(caseId ?? '');
  const client = caseRecord ? mockGetClientById(caseRecord.clientId) : null;
  const goals = mockGetGoalsByCaseId(caseId ?? '');
  const household = client ? mockGetHouseholdByClientId(client.id) : [];
  const interactions = mockGetInteractionsByCaseId(caseId ?? '');
  const followUp = mockGetFollowUp(caseId ?? '');
  const referrals = mockGetReferralsByCaseId(caseId ?? '');
  const documents = mockGetDocumentsByCaseId(caseId ?? '');
  const audit = mockGetAuditByCaseId(caseId ?? '');
  const resources = mockGetResources();
  const composition = computeComposition(household.map((m) => m.dateOfBirth));

  if (!caseRecord) {
    return (
      <Box sx={{ p: (t) => t.spacing(3) }}>
        <Alert severity="error" sx={{ mb: (t) => t.spacing(2) }}>
          Case not found. The case ID "{caseId}" does not exist or you do not have access.
        </Alert>
      </Box>
    );
  }

  const alerts = computeCaseAlerts(caseRecord, followUp, referrals);
  const isClosed = caseRecord.status === 'Closed';
  const [auditOpen, setAuditOpen] = useState(false);

  return (
    <Box sx={{ p: (t) => t.spacing(3) }} className="case-record">
      {/* Breadcrumbs + actions */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ sm: 'center' }}
        spacing={1}
        sx={{ mb: (t) => t.spacing(2) }}
        className="no-print"
      >
        <Breadcrumbs aria-label="Breadcrumb">
          <RouterLink
            to={buildPath('dashboard')}
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            <Typography variant="body2">Dashboard</Typography>
          </RouterLink>
          {isClosed && (
            <RouterLink
              to={buildPath('admin')}
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <Typography variant="body2">Admin</Typography>
            </RouterLink>
          )}
          <RouterLink
            to={buildPath('clients/search')}
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            <Typography variant="body2">Client Search</Typography>
          </RouterLink>
          <RouterLink
            to={buildPath(`clients/${caseRecord.clientId}`)}
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            <Typography variant="body2">{caseRecord.clientName}</Typography>
          </RouterLink>
          <Typography variant="body2" sx={{ color: (t) => t.palette.text.secondary }}>
            {caseRecord.caseId}
          </Typography>
        </Breadcrumbs>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ArrowLeft />}
            onClick={() => navigateToPath('dashboard')}
          >
            Dashboard
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => navigateToPath(`clients/${caseRecord.clientId}`)}
          >
            Client Record
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Printer />}
            onClick={() => window.print()}
          >
            Print
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ClipboardListOutline />}
            onClick={() => setAuditOpen(true)}
          >
            Audit Log
          </Button>
        </Stack>
      </Stack>

      {/* Banner Header */}
      <Card sx={{ mb: (t) => t.spacing(2) }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ md: 'center' }}
          justifyContent="space-between"
          sx={{
            p: (t) => t.spacing(2),
            background: (t) => t.palette.background.paper,
          }}
        >
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: (t) => t.spacing(0.5) }}>
              <Typography variant="h6" fontWeight={700}>
                {caseRecord.caseId}
              </Typography>
              <CaseStatusBadge status={caseRecord.status} />
              <TierBadge tier={caseRecord.tier} />
            </Stack>
            <Typography variant="body2" fontWeight={600}>
              {caseRecord.clientName}
            </Typography>
            <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>
              {caseRecord.program} &middot; {caseRecord.progression} &middot; Managed by{' '}
              {caseRecord.assignedManager}
            </Typography>
          </Box>
          {/* Right side: military badge + alert flags */}
          <Stack alignItems="flex-end" spacing={1}>
            {client?.militaryBranch && (
              <Box
                sx={{
                  textAlign: 'center',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  bgcolor: (t) => t.palette.primary.light,
                  border: 1,
                  borderColor: (t) => t.palette.primary.main,
                }}
              >
                <Typography
                  variant="overline"
                  sx={{ fontSize: 9, color: (t) => t.palette.primary.dark, display: 'block', lineHeight: 1.2 }}
                >
                  {client.militaryBranch}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, color: (t) => t.palette.primary.dark, fontSize: 11 }}
                >
                  {client.militaryRank ?? 'Veteran'}
                </Typography>
              </Box>
            )}
            {(alerts.length > 0 || isClosed) && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {isClosed && (
                  <Chip label="Case closed — read-only" size="small" color="info" />
                )}
                {alerts.map((alert) => (
                  <Chip
                    key={alert.message}
                    label={alert.message}
                    size="small"
                    color={alert.severity === 'error' ? 'error' : alert.severity === 'warning' ? 'warning' : 'info'}
                  />
                ))}
              </Stack>
            )}
          </Stack>
        </Stack>
      </Card>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: (t) => t.palette.divider }} className="no-print">
        <Tabs
          value={activeTab}
          onChange={(_e, newValue: CaseTabId) => handleTabChange(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="Case record tabs"
        >
          {CASE_TABS.filter((tab) => tab.id !== 'audit' && tab.id !== 'household').map((tab) => (
            <Tab key={tab.id} value={tab.id} label={tab.label} />
          ))}
        </Tabs>
      </Box>

      {/* Tab Panels (locked when the case is closed) */}
      <Box
        sx={
          isClosed
            ? { pointerEvents: 'none', opacity: 0.75 }
            : undefined
        }
      >
        <TabPanel activeTab={activeTab} tabId="summary">
          <SummaryTab
            caseRecord={caseRecord}
            client={client}
          goals={goals}
          referrals={referrals}
          documents={documents}
          interactions={interactions}
          audit={audit}
          composition={composition}
        />
        </TabPanel>


        <TabPanel activeTab={activeTab} tabId="profile">
          <CaseProfileTab caseRecord={caseRecord} />
        </TabPanel>

        <TabPanel activeTab={activeTab} tabId="intake">
          <ClientIntakeTab caseRecord={caseRecord} client={client} verifierName={currentUser.name} household={household} clientId={caseRecord.clientId} />
        </TabPanel>

        <TabPanel activeTab={activeTab} tabId="household">
          <HouseholdTab household={household} clientId={caseRecord.clientId} />
        </TabPanel>

        <TabPanel activeTab={activeTab} tabId="assessment">
          <AssessmentTab client={client} />
        </TabPanel>

        <TabPanel activeTab={activeTab} tabId="housing">
          <HousingTab caseRecord={caseRecord} client={client} />
        </TabPanel>

        <TabPanel activeTab={activeTab} tabId="insurance">
          <InsuranceTab caseRecord={caseRecord} />
        </TabPanel>

        <TabPanel activeTab={activeTab} tabId="assistance">
          <AssistanceTab caseRecord={caseRecord} />
        </TabPanel>

        <TabPanel activeTab={activeTab} tabId="service-plan">
          <RecoveryPlanTab caseRecord={caseRecord} goals={goals} clientId={caseRecord.clientId} />
        </TabPanel>

        <TabPanel activeTab={activeTab} tabId="referrals">
          <ReferralsTab
            referrals={referrals}
            resources={resources}
            caseId={caseRecord.caseId}
            clientId={caseRecord.clientId}
          />
        </TabPanel>

        <TabPanel activeTab={activeTab} tabId="notes">
          <NotesFollowUpTab
            caseRecord={caseRecord}
            interactions={interactions}
            followUp={followUp}
            goals={goals}
            referrals={referrals}
          />
        </TabPanel>

        <TabPanel activeTab={activeTab} tabId="documents">
          <DocumentsTab documents={documents} />
        </TabPanel>

        <TabPanel activeTab={activeTab} tabId="benefits">
          <IstatTab caseRecord={caseRecord} client={client} />
        </TabPanel>

        <TabPanel activeTab={activeTab} tabId="closure">
          <ClosureTab caseRecord={caseRecord} />
        </TabPanel>
      </Box>

      {/* Audit Log Dialog */}
      <Dialog open={auditOpen} onClose={() => setAuditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Audit Log — {caseRecord.caseId}</DialogTitle>
        <DialogContent dividers>
          <AuditTab audit={audit} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAuditOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
